"use client";

import type {
  AdminService,
  AgentService,
  AuthService,
  BenchmarkService,
  ChatService,
  DashboardService,
  FileService,
  ProviderService,
  ProfileService,
  RitualService,
  SessionService,
  SkillService,
  WorkflowService
} from "@/services/contracts/services";
import type {
  AgentSummary,
  ApprovalStatus,
  Attachment,
  AuthSession,
  ChartSpec,
  ChatMessage,
  ChatSession,
  CompanyBenchmark,
  CustomBenchmark,
  CustomDataAsset,
  DashboardStats,
  PeerGroupsData,
  PeerInfo,
  ProviderDisconnectResult,
  ProviderId,
  RoutingProviderId,
  ProviderLoginSession,
  ProviderPreferences,
  ProviderRuntimeStatus,
  ProviderStatusSummary,
  RawLlmLoggingPreference,
  Ritual,
  SharedSessionLookup,
  SkillCatalog,
  SkillCatalogItem,
  SkillProviderId,
  SkillSyncResult,
  ToolCall,
  MessageArtifact,
  UploadedFile,
  User,
  Workflow,
  WorkflowDraft,
  WorkflowGenerationInput,
  WorkflowSummary
} from "@/services/contracts/types";
import type { ThemeId } from "@/features/theme/themes";

const DEFAULT_API_BASE_URL = "/backend-api";
const AUTH_STORAGE_KEY = "stella-ui-auth";
const ASYNC_CHAT_PENDING_STORAGE_KEY = "stella-ui-async-chat-pending-jobs";

const TOOL_DISPLAY_NAME_MAP: Record<string, string> = {
  FetchDate: "Date Fetcher",
  GetFactorMnemonics: "QES Factor Definition Module",
  GetFactorLandscape: "Factor Landscape Analyzer",
  AnalyzePortfolioRisk: "Portfolio Risk Analyzer",
  AnalyzePortfolioExposure: "Portfolio Exposure Analyzer",
  RunFactorScreen: "Factor Screen Runner",
  ResolveFactor: "Factor Catalog",
  SearchSecurity: "Security Search",
  ListPortfolios: "Portfolio Catalog",
  GetCompanyFactorData: "Company QES Factor Dataset",
  GetFactorDataForUniverse: "Universe QES Factor Dataset",
  RegisterDataFrame: "DataFrame Registration Module",
  FilterByQuery: "Query Filter",
  FilterByRank: "Rank Filter",
  GetUniqueValues: "Unique Value Set",
  GetColumnNames: "Column Catalog",
  GetRowCount: "Row Count Metric",
  DataFrameFromReference: "Data Retrieval Module",
  PlotDataFrame: "Visualization Engine",
  PlotFlexibleData: "Flexible Visualization Engine",
  plot_flexible_data: "Flexible Visualization Engine",
  WriteArtifact: "Artifact Writer",
  write_artifact: "Artifact Writer",
  GetCorporateSectionMappings: "Corporate Section Map",
  GetCorporateFilings: "Corporate Filings Archive",
  get_equity_fundamental_research: "Wolfe Fundamental Research Notes",
  get_macro_research: "Wolfe Macro Research Notes",
  get_equity_quant_research: "QES Research Notes",
  get_call_transcripts: "Call Transcript Archive",
  GetCompanyIntradayPrice: "Company Intraday Price Feed",
  GetUniverseIntradayPrice: "Universe Intraday Price Feed",
  GetIntradayFactorCumulativePerformance: "Intraday Factor Performance Tracker",
  ComputeIntradayReturns: "Intraday Returns Calculator",
  GetBaseScreen: "Baseline Equity Screen",
  get_peers: "Peer Group Finder",
  get_prior_rating_and_price_target: "Wolfe Prior Ratings & Price Targets",
  get_today_date: "Date Fetcher",
  get_performance_data: "Performance Data Fetcher",
  get_PIT_consensus: "Consensus Estimates",
  get_fiscal_year_end: "Fiscal Year End Fetcher"
};

const TOOL_DISPLAY_NAME_MAP_LOWER: Record<string, string> = Object.fromEntries(
  Object.entries(TOOL_DISPLAY_NAME_MAP).map(([key, value]) => [key.toLowerCase(), value])
);

const MCP_TOOL_NAME_PREFIX_PATTERN = /^mcp__.+?__(.+)$/i;

type RequestOptions = {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  auth?: boolean;
  authToken?: string;
  headers?: Record<string, string>;
};

type BackendFileReference = {
  reference_id?: string;
  name?: string;
  uri?: string;
  display_name?: string;
  original_name?: string;
  mime_type?: string;
  size_bytes?: number;
  upload_time?: string;
  local_path?: string;
};

type BackendChartPayload = {
  html?: string;
  metadata?: Record<string, unknown>;
};

type BackendArtifactPayload = {
  artifact_ref?: unknown;
  ref?: unknown;
  title?: unknown;
  kind?: unknown;
  language?: unknown;
  content?: unknown;
  created_at?: unknown;
  chart_spec?: unknown;
};

type BackendChartSpecPayload = {
  version?: unknown;
  id?: unknown;
  title?: unknown;
  subtitle?: unknown;
  chartType?: unknown;
  xKey?: unknown;
  xType?: unknown;
  xAxisLabel?: unknown;
  yAxisLabel?: unknown;
  y2AxisLabel?: unknown;
  data?: unknown;
  series?: unknown;
  meta?: unknown;
  height?: unknown;
};

type ChatMessageApiResponse = {
  success: boolean;
  response_data?: {
    text?: string;
    thinking?: unknown[];
    tool_calls?: unknown[];
    charts?: BackendChartPayload[];
    chart_specs?: BackendChartSpecPayload[];
    artifacts?: BackendArtifactPayload[];
  };
  error?: string;
};

type ChartHtmlApiResponse = {
  success: boolean;
  chart_ref?: string;
  html?: string;
  mime_type?: string;
  error?: string;
};

type AsyncChatStartResponse = {
  success: boolean;
  job_id?: string;
  status?: "queued" | "running" | "completed" | "failed";
  max_runtime_seconds?: number;
  poll_url?: string;
  error?: string;
};

type AsyncChatProgressPayload = {
  timestamp?: unknown;
  message?: unknown;
};

type AsyncChatStatusResponse = {
  success: boolean;
  job_id?: string;
  status?: "queued" | "running" | "completed" | "failed";
  progress?: AsyncChatProgressPayload[];
  result?: ChatMessageApiResponse;
  error?: string;
};

type PendingAsyncChatJob = {
  jobId: string;
  sessionId: string;
  createdAtMs: number;
  updatedAtMs: number;
  userMessage?: Record<string, unknown>;
};

type BackendProviderStatusPayload = {
  authenticated?: unknown;
  exit_code?: unknown;
  details?: unknown;
  plan?: unknown;
  allowed_models?: unknown;
};

type BackendProvidersStatusResponse = {
  success: boolean;
  user?: unknown;
  preferred_provider?: unknown;
  preferred_model?: unknown;
  updated_at?: unknown;
  providers?: Record<string, BackendProviderStatusPayload>;
  allowed_models?: Record<string, unknown>;
  provider_plans?: Record<string, unknown>;
};

type BackendProviderPreferencesResponse = {
  success: boolean;
  user?: unknown;
  preferred_provider?: unknown;
  preferred_model?: unknown;
  updated_at?: unknown;
};

type BackendProviderAuthStatusResponse = {
  success: boolean;
  user?: unknown;
  provider?: unknown;
  authenticated?: unknown;
  exit_code?: unknown;
  details?: unknown;
};

type BackendProviderLoginSessionResponse = {
  login_id?: unknown;
  user?: unknown;
  provider?: unknown;
  status?: unknown;
  auth_url?: unknown;
  device_code?: unknown;
  exit_code?: unknown;
  created_at?: unknown;
  updated_at?: unknown;
  output_tail?: unknown;
  authenticated?: unknown;
};

type BackendProviderDisconnectResponse = BackendProvidersStatusResponse & {
  message?: unknown;
  cancelled_login_ids?: unknown;
};

type BackendSkillCatalogResponse = {
  success: boolean;
  generated_at?: unknown;
  repo_revision?: unknown;
  skills?: unknown[];
  message?: unknown;
};

type BackendSkillSyncResponse = {
  success: boolean;
  message?: unknown;
  result?: unknown;
};

type BackendRawLlmLoggingPreferenceResponse = {
  success: boolean;
  enabled?: unknown;
  message?: unknown;
};

function makeId(prefix: string): string {
  return `${prefix}-${Math.random().toString(36).slice(2, 9)}`;
}

function getApiBaseUrl(): string {
  const raw = process.env.NEXT_PUBLIC_STELLA_API_BASE_URL?.trim() || DEFAULT_API_BASE_URL;
  return raw.replace(/\/+$/, "");
}

function formatFileSize(bytes: number | undefined): string {
  if (!bytes || bytes <= 0) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 102.4) / 10} KB`;
  return `${Math.round(bytes / (1024 * 102.4)) / 10} MB`;
}

function cleanOptionalText(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  if (!trimmed || trimmed === "Not Specified") return undefined;
  return trimmed;
}

function normalizeStatus(value: unknown): ApprovalStatus {
  if (value === "pending" || value === "approved" || value === "rejected") {
    return value;
  }
  return "approved";
}

function normalizeDate(value: unknown): string {
  const now = new Date().toISOString();
  if (value === null || value === undefined) return now;

  if (typeof value === "number" && Number.isFinite(value)) {
    const ms = value > 1_000_000_000_000 ? value : value * 1000;
    if (ms < 946_684_800_000) return now;
    return new Date(ms).toISOString();
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return now;

    if (/^\d+(\.\d+)?$/.test(trimmed)) {
      return normalizeDate(Number(trimmed));
    }

    const parsed = Date.parse(trimmed);
    if (!Number.isNaN(parsed)) return new Date(parsed).toISOString();
  }

  return now;
}

function normalizeSessionId(rawId: string): string {
  let next = rawId.trim();
  if (!next) return next;

  for (let i = 0; i < 2; i += 1) {
    try {
      const decoded = decodeURIComponent(next);
      if (decoded === next) break;
      next = decoded;
    } catch {
      break;
    }
  }

  return next;
}

function normalizeProviderId(value: unknown, fallback: ProviderId = "codex"): ProviderId {
  return value === "claude" || value === "codex" ? value : fallback;
}

function normalizeRoutingProviderId(value: unknown, fallback: RoutingProviderId = "gemini"): RoutingProviderId {
  if (value === "gemini" || value === "claude" || value === "codex") return value;
  return fallback;
}

function normalizeProviderLoginStatus(value: unknown): ProviderLoginSession["status"] {
  if (
    value === "pending" ||
    value === "running" ||
    value === "authenticated" ||
    value === "failed" ||
    value === "cancelled" ||
    value === "completed_unverified" ||
    value === "timeout"
  ) {
    return value;
  }
  return "running";
}

function normalizeEpochSeconds(value: unknown): number {
  const now = Math.floor(Date.now() / 1000);
  if (typeof value === "number" && Number.isFinite(value)) {
    return value > 1_000_000_000_000 ? Math.floor(value / 1000) : value;
  }
  if (typeof value === "string" && value.trim()) {
    const parsedNumber = Number(value);
    if (Number.isFinite(parsedNumber)) {
      return parsedNumber > 1_000_000_000_000 ? Math.floor(parsedNumber / 1000) : parsedNumber;
    }
  }
  return now;
}

function normalizeExitCode(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

function normalizeModelList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const normalized: string[] = [];
  for (const entry of value) {
    if (typeof entry !== "string") continue;
    const cleaned = entry.trim();
    if (!cleaned || normalized.includes(cleaned)) continue;
    normalized.push(cleaned);
  }
  return normalized;
}

function mapProviderRuntimeStatus(
  provider: ProviderId,
  payload: unknown,
  fallbackAllowedModels: string[] = []
): ProviderRuntimeStatus {
  const raw = (payload ?? {}) as Record<string, unknown>;
  const allowedModels = normalizeModelList(raw.allowed_models ?? raw.allowedModels);
  return {
    provider,
    authenticated: raw.authenticated === true,
    exitCode: normalizeExitCode(raw.exit_code ?? raw.exitCode),
    details: typeof raw.details === "string" ? raw.details : "",
    plan: cleanOptionalText(raw.plan),
    allowedModels: allowedModels.length ? allowedModels : fallbackAllowedModels
  };
}

function mapProviderPreferences(payload: BackendProviderPreferencesResponse): ProviderPreferences {
  return {
    user: typeof payload.user === "string" ? payload.user : "unknown",
    preferredProvider: normalizeRoutingProviderId(payload.preferred_provider),
    preferredModel: cleanOptionalText(payload.preferred_model),
    updatedAt: normalizeEpochSeconds(payload.updated_at)
  };
}

function mapProviderStatusSummary(payload: BackendProvidersStatusResponse): ProviderStatusSummary {
  const preferredProvider = normalizeRoutingProviderId(payload.preferred_provider);
  const providers = payload.providers ?? {};
  const allowedModelMapPayload = payload.allowed_models ?? {};
  const providerPlansPayload = payload.provider_plans ?? {};
  const codexAllowedModels = normalizeModelList(allowedModelMapPayload.codex ?? providers.codex?.allowed_models);
  const claudeAllowedModels = normalizeModelList(allowedModelMapPayload.claude ?? providers.claude?.allowed_models);

  return {
    user: typeof payload.user === "string" ? payload.user : "unknown",
    preferredProvider,
    preferredModel: cleanOptionalText(payload.preferred_model),
    updatedAt: normalizeEpochSeconds(payload.updated_at),
    providers: {
      codex: mapProviderRuntimeStatus("codex", providers.codex, codexAllowedModels),
      claude: mapProviderRuntimeStatus("claude", providers.claude, claudeAllowedModels)
    },
    allowedModelsByProvider: {
      gemini: normalizeModelList(allowedModelMapPayload.gemini),
      codex: codexAllowedModels,
      claude: claudeAllowedModels
    },
    providerPlans: {
      codex: cleanOptionalText(providerPlansPayload.codex ?? providers.codex?.plan),
      claude: cleanOptionalText(providerPlansPayload.claude ?? providers.claude?.plan)
    }
  };
}

function mapProviderLoginSession(
  payload: BackendProviderLoginSessionResponse,
  fallbackProvider: ProviderId = "codex"
): ProviderLoginSession {
  const provider = normalizeProviderId(payload.provider, fallbackProvider);
  return {
    loginId: typeof payload.login_id === "string" ? payload.login_id : makeId("provider-login"),
    user: typeof payload.user === "string" ? payload.user : "unknown",
    provider,
    status: normalizeProviderLoginStatus(payload.status),
    authUrl: cleanOptionalText(payload.auth_url),
    deviceCode: cleanOptionalText(payload.device_code),
    exitCode: normalizeExitCode(payload.exit_code),
    createdAt: normalizeEpochSeconds(payload.created_at),
    updatedAt: normalizeEpochSeconds(payload.updated_at),
    outputTail: typeof payload.output_tail === "string" ? payload.output_tail : "",
    authenticated: typeof payload.authenticated === "boolean" ? payload.authenticated : null
  };
}

function normalizeSkillProvider(value: unknown): SkillProviderId | null {
  if (value === "gemini" || value === "codex" || value === "claude") return value;
  return null;
}

function normalizeSkillProviders(value: unknown): SkillProviderId[] {
  if (!Array.isArray(value)) return ["gemini", "codex", "claude"];
  const providers = value
    .map((item) => normalizeSkillProvider(item))
    .filter((item): item is SkillProviderId => item !== null);
  const deduped = Array.from(new Set(providers));
  return deduped.length ? deduped : ["gemini", "codex", "claude"];
}

function mapSkillCatalogItem(payload: unknown): SkillCatalogItem | null {
  if (!isRecord(payload)) return null;
  const id = cleanOptionalText(payload.id);
  const name = cleanOptionalText(payload.name);
  const plugin = cleanOptionalText(payload.plugin);
  const domain = cleanOptionalText(payload.domain);
  const workflowId = cleanOptionalText(payload.workflow_id ?? payload.workflowId);
  if (!id || !name || !plugin || !domain || !workflowId) return null;

  const commands = Array.isArray(payload.commands)
    ? payload.commands
        .filter((item): item is string => typeof item === "string")
        .map((item) => item.trim())
        .filter(Boolean)
    : [];

  return {
    id,
    name,
    plugin,
    domain,
    description: cleanOptionalText(payload.description) ?? "",
    commands,
    providers: normalizeSkillProviders(payload.providers),
    workflowId,
    sourcePath: cleanOptionalText(payload.source_path ?? payload.sourcePath),
    enabled: payload.enabled === true,
    updatedAt: normalizeDate(payload.updated_at ?? payload.updatedAt)
  };
}

function mapSkillSyncResult(payload: unknown): SkillSyncResult {
  const raw = (payload ?? {}) as Record<string, unknown>;
  return {
    username: cleanOptionalText(raw.username) ?? "unknown",
    repoRevision: cleanOptionalText(raw.repo_revision ?? raw.repoRevision) ?? "",
    repoPath: cleanOptionalText(raw.repo_path ?? raw.repoPath) ?? "",
    pluginCount: typeof raw.plugin_count === "number" ? raw.plugin_count : 0,
    skillCount: typeof raw.skill_count === "number" ? raw.skill_count : 0,
    workflowUpserts: typeof raw.workflow_upserts === "number" ? raw.workflow_upserts : 0,
    enabledSkillCount: typeof raw.enabled_skill_count === "number" ? raw.enabled_skill_count : 0
  };
}

function mapUser(payload: unknown): User {
  const raw = (payload ?? {}) as Record<string, unknown>;
  const username = typeof raw.username === "string" ? raw.username : "";
  return {
    username,
    email: typeof raw.email === "string" ? raw.email : "",
    department: cleanOptionalText(raw.department),
    analyst: cleanOptionalText(raw.analyst),
    role: raw.role === "admin" || raw.is_admin === true ? "admin" : "user",
    status: normalizeStatus(raw.status),
    createdAt: normalizeDate(raw.created_at ?? raw.createdAt)
  };
}

function pickString(raw: Record<string, unknown>, ...keys: string[]): string {
  for (const key of keys) {
    const value = raw[key];
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }
  return "";
}

function mapPeerInfo(entry: unknown): PeerInfo | null {
  if (!isRecord(entry)) return null;
  const ticker = pickString(entry, "ticker", "PEER", "peer");
  if (!ticker) return null;

  const primaryRaw = entry.isPrimary ?? entry.is_primary ?? entry.PRIMARY ?? entry.primary;
  const isPrimary =
    primaryRaw === true ||
    primaryRaw === "Y" ||
    primaryRaw === "y" ||
    primaryRaw === "true" ||
    primaryRaw === "True";

  const rankRaw = entry.rank ?? entry.RANK;
  const rankValue =
    typeof rankRaw === "number"
      ? rankRaw
      : typeof rankRaw === "string" && rankRaw.trim()
        ? Number(rankRaw)
        : Number.NaN;
  const rank = Number.isFinite(rankValue) ? rankValue : null;

  return {
    ticker,
    isPrimary,
    rank
  };
}

function mapCustomBenchmarkEntry(entry: unknown): CustomBenchmark | null {
  if (!isRecord(entry)) return null;
  const id = pickString(entry, "id", "BENCH_ID", "bench_id");
  const name = pickString(entry, "name", "BENCH_NAME", "bench_name");
  if (!id || !name) return null;

  const rawTickers = entry.tickers;
  const tickers = Array.isArray(rawTickers)
    ? rawTickers.filter((item): item is string => typeof item === "string").map((item) => item.trim()).filter(Boolean)
    : [];

  const type = pickString(entry, "type", "WEIGHTING_SCHEME", "weighting", "TYPE") || "CUSTOM";

  return {
    id,
    name,
    description: pickString(entry, "description", "DESCRIPTION"),
    type,
    tickers,
    createdAt: normalizeDate(entry.createdAt ?? entry.created_at)
  };
}

function mapCoverageCompanyEntry(entry: unknown): CompanyBenchmark | null {
  if (!isRecord(entry)) return null;
  const ticker = pickString(entry, "ticker", "TICKER");
  if (!ticker) return null;

  return {
    ticker,
    companyName: pickString(entry, "companyName", "COMPANY_NAME", "name") || `${ticker} Inc.`,
    sector: pickString(entry, "sector", "SECTOR"),
    industry: pickString(entry, "industry", "INDUSTRY"),
    benchmarks: []
  };
}

function extractBenchmarkName(entry: unknown): string {
  if (!isRecord(entry)) return "";
  return pickString(entry, "name", "BENCH_NAME", "bench_name", "BENCH_ID", "bench_id");
}

function mapToolCalls(payload: unknown): ToolCall[] | undefined {
  if (!Array.isArray(payload) || !payload.length) return undefined;
  const toolCalls: ToolCall[] = payload.map((item, index) => {
    if (typeof item === "string") {
      return { id: `${makeId("tool")}-${index}`, label: toToolDisplayName(item), status: "done" };
    }
    const raw = item as Record<string, unknown>;
    const rawLabel =
      (typeof raw.label === "string" && raw.label) ||
      (typeof raw.name === "string" && raw.name) ||
      (typeof raw.tool === "string" && raw.tool) ||
      `Tool ${index + 1}`;
    const status = raw.status === "queued" || raw.status === "running" || raw.status === "done" ? raw.status : "done";
    return {
      id: typeof raw.id === "string" && raw.id ? raw.id : `${makeId("tool")}-${index}`,
      label: toToolDisplayName(rawLabel),
      status
    };
  });
  return toolCalls.length ? toolCalls : undefined;
}

function toToolDisplayName(rawToolName: string): string {
  const trimmed = rawToolName.trim();
  if (!trimmed) return rawToolName;

  const normalized = normalizeToolName(trimmed);
  const candidates = new Set<string>([trimmed, normalized]);

  if (normalized.includes("_")) {
    candidates.add(
      normalized
        .split("_")
        .filter(Boolean)
        .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
        .join("")
    );
  } else {
    candidates.add(normalized.replace(/([a-z0-9])([A-Z])/g, "$1_$2").toLowerCase());
  }

  for (const candidate of candidates) {
    const direct = TOOL_DISPLAY_NAME_MAP[candidate];
    if (direct) return direct;

    const normalizedCase = TOOL_DISPLAY_NAME_MAP_LOWER[candidate.toLowerCase()];
    if (normalizedCase) return normalizedCase;
  }

  return normalized || trimmed;
}

function normalizeToolName(value: string): string {
  let normalized = value.trim();
  if (!normalized) return normalized;

  normalized = normalized.replace(/\s+\[\d+(?:\s*,\s*\d+)*\]$/, "").trim();
  normalized = normalized.replace(/\s+\((?:done|running|queued)\)$/i, "").trim();

  const mcpMatch = normalized.match(MCP_TOOL_NAME_PREFIX_PATTERN);
  if (mcpMatch?.[1]?.trim()) {
    normalized = mcpMatch[1].trim();
  }

  const separatorCandidates = [".", "/", ":"];
  for (const separator of separatorCandidates) {
    const index = normalized.lastIndexOf(separator);
    if (index > -1 && index < normalized.length - 1) {
      const suffix = normalized.slice(index + 1).trim();
      if (suffix && !suffix.includes(" ")) {
        normalized = suffix;
      }
    }
  }

  return normalized;
}

function normalizeMessageFeedback(value: unknown): string | undefined {
  if (typeof value === "string" && value.trim()) return value.trim();
  if (!value || typeof value !== "object") return undefined;
  const raw = value as Record<string, unknown>;
  if (typeof raw.rating === "string" && raw.rating.trim()) return raw.rating.trim();
  if (typeof raw.value === "string" && raw.value.trim()) return raw.value.trim();
  return undefined;
}

function mapReasoning(payload: unknown): string[] | undefined {
  if (!Array.isArray(payload) || !payload.length) return undefined;
  const reasoning = payload
    .map((item) => {
      if (typeof item === "string") {
        return item.trim();
      }
      if (!item || typeof item !== "object") {
        return "";
      }
      const raw = item as Record<string, unknown>;
      const candidates = [raw.text, raw.thinking, raw.reasoning, raw.content];
      for (const candidate of candidates) {
        if (typeof candidate === "string" && candidate.trim().length > 0) {
          return candidate.trim();
        }
      }
      return "";
    })
    .filter((item) => item.length > 0);
  return reasoning.length ? reasoning : undefined;
}

function stripChartReferenceHtml(content: string): string {
  return content
    .replace(/<div\s+data-chart-ref=(["'])[^"']*\1[\s\S]*?<\/div>/gi, "")
    .replace(/<div\s+data-artifact-ref=(["'])[^"']*\1[\s\S]*?<\/div>/gi, "")
    .replace(/```(?:\w+)?\s*```/g, "")
    .trim();
}

function extractChartReferenceFromHtml(html: string): string | null {
  const chartRefAttrMatch = html.match(/data-chart-ref=(["'])([^"']+)\1/i);
  if (chartRefAttrMatch?.[2]?.trim()) {
    return chartRefAttrMatch[2].trim();
  }

  const chartRefTextMatch = html.match(/Chart Reference:\s*([A-Za-z0-9._:-]+)/i);
  if (chartRefTextMatch?.[1]?.trim()) {
    return chartRefTextMatch[1].trim();
  }

  return null;
}

function extractChartReferenceFromMetadata(metadata: unknown): string | null {
  if (!isRecord(metadata)) return null;
  const candidateKeys = ["chart_ref", "chartRef", "ref", "reference"] as const;
  for (const key of candidateKeys) {
    const value = metadata[key];
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }
  return null;
}

function mapArtifactKind(rawKind: unknown): MessageArtifact["kind"] {
  if (typeof rawKind === "string") {
    const normalized = rawKind.trim().toLowerCase();
    if (normalized === "html" || normalized === "code" || normalized === "document") {
      return normalized;
    }
  }
  return "document";
}

function mapArtifactPayload(payload: unknown): MessageArtifact | null {
  if (!isRecord(payload)) return null;

  const rawContent = typeof payload.content === "string" ? payload.content : "";
  const trimmedContent = rawContent.trim();
  const chartSpec = mapChartSpec(payload.chart_spec ?? payload.chartSpec);
  if (!trimmedContent && chartSpec === null) return null;

  const rawRef =
    (typeof payload.artifact_ref === "string" && payload.artifact_ref.trim()) ||
    (typeof payload.ref === "string" && payload.ref.trim()) ||
    undefined;
  const rawTitle = typeof payload.title === "string" ? payload.title.trim() : "";
  const kind = mapArtifactKind(payload.kind);
  const fallbackTitle =
    kind === "html"
      ? "HTML Artifact"
      : kind === "code"
        ? "Code Artifact"
        : "Document Artifact";

  return {
    ref: rawRef,
    title: rawTitle || fallbackTitle,
    content: trimmedContent || (chartSpec ? JSON.stringify(chartSpec, null, 2) : ""),
    kind,
    language: typeof payload.language === "string" && payload.language.trim() ? payload.language.trim() : undefined,
    chartSpec: chartSpec ?? undefined,
    createdAt:
      typeof payload.created_at === "string" && payload.created_at.trim() ? payload.created_at.trim() : undefined
  };
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function buildChartReferenceHtml(chartRef: string): string {
  const safeRef = escapeHtml(chartRef);
  return [
    `<div data-chart-ref="${safeRef}" style="padding: 20px; border: 2px dashed #c2c9d4; border-radius: 12px; text-align: center; font-family: Arial, sans-serif; color: #334155;">`,
    `<h3 style="margin: 0 0 8px 0; font-size: 16px;">Chart Reference: ${safeRef}</h3>`,
    "<p style=\"margin: 0; font-size: 13px;\">Rendering chart...</p>",
    "</div>"
  ].join("");
}

function parseReasoningFinalBlocks(content: string): { answer: string; reasoning: string[] } | null {
  const match = content.match(/\/\*REASONING\*\/([\s\S]*?)\/\*FINAL_ANSWER\*\/([\s\S]*)/i);
  if (!match) return null;
  const reasoningRaw = match[1]?.trim() ?? "";
  const answer = (match[2]?.trim() ?? "") || stripChartReferenceHtml(content);
  const reasoning = reasoningRaw
    .split(/\n{2,}/)
    .map((item) => item.trim())
    .filter((item) => item.length > 0);
  return { answer, reasoning };
}

function mapChartSpec(payload: unknown): ChartSpec | null {
  if (!isRecord(payload)) return null;

  const version = typeof payload.version === "string" && payload.version.trim() ? payload.version.trim() : "1.0";
  const id = typeof payload.id === "string" && payload.id.trim() ? payload.id.trim() : makeId("chart-spec");
  const chartTypeRaw = typeof payload.chartType === "string" ? payload.chartType.trim().toLowerCase() : "";
  const chartType: ChartSpec["chartType"] =
    chartTypeRaw === "line" ||
    chartTypeRaw === "bar" ||
    chartTypeRaw === "area" ||
    chartTypeRaw === "composed" ||
    chartTypeRaw === "scatter"
      ? chartTypeRaw
      : "line";
  const xKey = typeof payload.xKey === "string" && payload.xKey.trim() ? payload.xKey.trim() : "x";
  const xTypeRaw = typeof payload.xType === "string" ? payload.xType.trim().toLowerCase() : "";
  const xType: ChartSpec["xType"] =
    xTypeRaw === "time" || xTypeRaw === "number" || xTypeRaw === "category" ? xTypeRaw : "category";

  const rawData = Array.isArray(payload.data) ? payload.data : [];
  const data = rawData
    .filter(isRecord)
    .map((item) => {
      const row: Record<string, number | string | null> = {};
      for (const [key, value] of Object.entries(item)) {
        if (value === null) {
          row[key] = null;
          continue;
        }
        if (typeof value === "number" && Number.isFinite(value)) {
          if (key === xKey && xType !== "number") {
            row[key] = String(value);
          } else {
            row[key] = value;
          }
          continue;
        }
        if (typeof value === "string") {
          const compact = value.trim();
          if (!compact) {
            row[key] = "";
            continue;
          }
          if (key === xKey && xType !== "number") {
            row[key] = compact;
            continue;
          }
          const parsedNumber = Number(compact);
          row[key] = Number.isFinite(parsedNumber) ? parsedNumber : compact;
          continue;
        }
      }
      return row;
    });

  const rawSeries = Array.isArray(payload.series) ? payload.series : [];
  const series: ChartSpec["series"] = rawSeries
    .filter(isRecord)
    .map((item) => {
      const key = typeof item.key === "string" ? item.key.trim() : "";
      if (!key) return null;
      const typeRaw = typeof item.type === "string" ? item.type.trim().toLowerCase() : "";
      const type: "line" | "bar" | "area" | undefined =
        typeRaw === "line" || typeRaw === "bar" || typeRaw === "area" ? typeRaw : undefined;
      const yAxisIdRaw = typeof item.yAxisId === "string" ? item.yAxisId.trim().toLowerCase() : "";
      const yAxisId: "left" | "right" | undefined =
        yAxisIdRaw === "right" || yAxisIdRaw === "left" ? yAxisIdRaw : undefined;
      return {
        key,
        label: typeof item.label === "string" && item.label.trim() ? item.label.trim() : key,
        type,
        yAxisId,
        color: typeof item.color === "string" && item.color.trim() ? item.color.trim() : undefined,
        stackId: typeof item.stackId === "string" && item.stackId.trim() ? item.stackId.trim() : undefined
      };
    })
    .filter((item): item is NonNullable<typeof item> => item !== null);

  if (!data.length || !series.length) return null;

  return {
    version,
    id,
    title: typeof payload.title === "string" && payload.title.trim() ? payload.title.trim() : undefined,
    subtitle: typeof payload.subtitle === "string" && payload.subtitle.trim() ? payload.subtitle.trim() : undefined,
    chartType,
    xKey,
    xType,
    xAxisLabel: typeof payload.xAxisLabel === "string" && payload.xAxisLabel.trim() ? payload.xAxisLabel.trim() : undefined,
    yAxisLabel: typeof payload.yAxisLabel === "string" && payload.yAxisLabel.trim() ? payload.yAxisLabel.trim() : undefined,
    y2AxisLabel: typeof payload.y2AxisLabel === "string" ? payload.y2AxisLabel : null,
    data,
    series,
    meta: isRecord(payload.meta) ? payload.meta : undefined,
    height: typeof payload.height === "number" && Number.isFinite(payload.height) ? payload.height : undefined
  };
}

function mapHistoryMessage(sessionId: string, payload: unknown, index: number): ChatMessage {
  const raw = (payload ?? {}) as Record<string, unknown>;
  const chartHtml = typeof raw.data === "string" ? raw.data : undefined;
  const isChartMessage = raw.type === "image" && typeof chartHtml === "string" && chartHtml.trim().startsWith("<");
  const chartSpec = mapChartSpec(raw.chart_spec ?? raw.chartSpec);
  const isChartSpecMessage =
    (raw.type === "chart_spec" || raw.type === "image") && chartSpec !== null;
  const role: ChatMessage["role"] = raw.role === "assistant" ? "assistant" : "user";
  const rawContent = typeof raw.content === "string" ? raw.content : "";
  const mappedArtifact = mapArtifactPayload(raw.artifact ?? raw.artifact_payload ?? raw.artifactPayload);
  const parsed = role === "assistant" ? parseReasoningFinalBlocks(rawContent) : null;
  const content = role === "assistant"
    ? parsed?.answer ?? stripChartReferenceHtml(rawContent)
    : rawContent;
  return {
    id: typeof raw.id === "string" ? raw.id : `${sessionId}-${index}-${makeId("msg")}`,
    sessionId,
    role,
    content,
    createdAt: normalizeDate(raw.created_at ?? raw.createdAt ?? raw.timestamp),
    type: isChartMessage || isChartSpecMessage ? "chart" : "text",
    chartHtml: isChartMessage ? chartHtml : undefined,
    chartSpec: chartSpec ?? undefined,
    reasoning: parsed?.reasoning?.length
      ? parsed.reasoning
      : mapReasoning(raw.thinking ?? raw.reasoning ?? raw.thoughts),
    toolCalls: mapToolCalls(raw.tool_calls ?? raw.toolCalls),
    attachments: Array.isArray(raw.attachments) ? (raw.attachments as Attachment[]) : undefined,
    feedback: normalizeMessageFeedback(raw.feedback),
    artifact: mappedArtifact ?? undefined
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function toPersistedHistoryMessage(message: ChatMessage): Record<string, unknown> {
  const persisted: Record<string, unknown> = {
    id: message.id,
    role: message.role,
    content: message.content,
    created_at: message.createdAt
  };

  if (message.type === "chart" && typeof message.chartHtml === "string" && message.chartHtml.trim()) {
    const rawChartHtml = message.chartHtml.trim();
    const chartRef = extractChartReferenceFromHtml(rawChartHtml);
    const persistedChartHtml =
      chartRef !== null
        ? buildChartReferenceHtml(chartRef)
        : rawChartHtml.length > MAX_PERSISTED_CHART_HTML_CHARS
          ? ""
          : rawChartHtml;

    if (persistedChartHtml) {
      persisted.type = "image";
      persisted.data = persistedChartHtml;
      persisted.mime_type = "image/html";
    }
  }
  if (message.type === "chart" && message.chartSpec) {
    persisted.chart_spec = message.chartSpec;
    if (persisted.type !== "image") {
      persisted.type = "chart_spec";
    }
  }

  if (message.toolCalls?.length) {
    persisted.tool_calls = message.toolCalls.map((tool) => ({
      id: tool.id,
      label: tool.label,
      status: tool.status
    }));
  }

  if (message.reasoning?.length) {
    persisted.thinking = message.reasoning;
  }

  if (message.attachments?.length) {
    persisted.attachments = message.attachments;
  }

  if (message.replyTo) {
    persisted.reply_to = message.replyTo;
  }

  if (message.feedback) {
    persisted.feedback = message.feedback;
  }

  if (message.artifact) {
    persisted.artifact = {
      ref: message.artifact.ref,
      title: message.artifact.title,
      content: message.artifact.content,
      kind: message.artifact.kind,
      language: message.artifact.language,
      created_at: message.artifact.createdAt,
      chart_spec: message.artifact.chartSpec
    };
  }

  return persisted;
}

function buildAssistantMessagesFromResponse(
  normalizedSessionId: string,
  responseData: NonNullable<ChatMessageApiResponse["response_data"]>
): ChatMessage[] {
  const chartSpecs = Array.isArray(responseData.chart_specs) ? responseData.chart_specs : [];
  const mappedSpecs: ChartSpec[] = chartSpecs
    .map((item) => mapChartSpec(item))
    .filter((item): item is ChartSpec => item !== null);

  const charts = Array.isArray(responseData.charts) ? responseData.charts : [];
  const htmlChartMessages: ChatMessage[] = charts
    .filter((item) => typeof item?.html === "string" && item.html.trim().length > 0)
    .map((item, index) => {
      const chartRef = extractChartReferenceFromMetadata(item.metadata) ?? extractChartReferenceFromHtml(item.html!);
      const chartHtml = chartRef ? buildChartReferenceHtml(chartRef) : item.html!;
      return {
        id: makeId("msg"),
        sessionId: normalizedSessionId,
        role: "assistant",
        type: "chart",
        chartHtml,
        chartSpec: mappedSpecs[index],
        content: "",
        createdAt: new Date().toISOString()
      };
    });
  const chartSpecOnlyMessages: ChatMessage[] =
    htmlChartMessages.length === 0
      ? mappedSpecs.map((spec) => ({
          id: makeId("msg"),
          sessionId: normalizedSessionId,
          role: "assistant",
          type: "chart",
          chartSpec: spec,
          content: "",
          createdAt: new Date().toISOString()
        }))
      : [];
  const chartMessages = htmlChartMessages.length > 0 ? htmlChartMessages : chartSpecOnlyMessages;
  const artifacts = Array.isArray(responseData.artifacts) ? responseData.artifacts : [];
  const artifactMessages: ChatMessage[] = artifacts
    .map((item) => mapArtifactPayload(item))
    .filter((item): item is MessageArtifact => item !== null)
    .map((artifact) => ({
      id: makeId("msg"),
      sessionId: normalizedSessionId,
      role: "assistant",
      type: "text",
      content: "",
      createdAt: artifact.createdAt ?? new Date().toISOString(),
      artifact
    }));

  const assistantMessages: ChatMessage[] = [];
  const assistantTextRaw = typeof responseData.text === "string" ? responseData.text : "";
  const parsedAssistant = parseReasoningFinalBlocks(assistantTextRaw);
  const assistantText = parsedAssistant?.answer ?? stripChartReferenceHtml(assistantTextRaw);
  const mappedReasoning = parsedAssistant?.reasoning?.length
    ? parsedAssistant.reasoning
    : mapReasoning(responseData.thinking);
  const mappedToolCalls = mapToolCalls(responseData.tool_calls);
  const shouldEmitAssistantText =
    assistantText.trim().length > 0 ||
    (chartMessages.length === 0 && artifactMessages.length === 0) ||
    mappedReasoning.length > 0 ||
    mappedToolCalls.length > 0;
  if (shouldEmitAssistantText) {
    assistantMessages.push({
      id: makeId("msg"),
      sessionId: normalizedSessionId,
      role: "assistant",
      type: "text",
      content: assistantText.trim().length > 0 ? assistantText : "",
      createdAt: new Date().toISOString(),
      reasoning: mappedReasoning,
      toolCalls: mappedToolCalls
    });
  }
  assistantMessages.push(...chartMessages);
  assistantMessages.push(...artifactMessages);

  return assistantMessages;
}

const DUPLICATE_MESSAGE_WINDOW_MS = 2 * 60 * 1000;

function normalizeMessageSignatureText(value: string): string {
  return value.trim().replace(/\s+/g, " ");
}

function parseIsoTimestampMs(value: string): number | null {
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function chartSpecSignature(spec: ChatMessage["chartSpec"]): string {
  if (!spec) return "";
  const seriesKey = spec.series
    .map((item) => `${item.key}:${item.type ?? ""}:${item.yAxisId ?? ""}:${item.stackId ?? ""}`)
    .join("|");
  return `${spec.id}|${spec.chartType}|${spec.xKey}|${seriesKey}|${spec.data.length}`;
}

function artifactSignature(artifact: ChatMessage["artifact"]): string {
  if (!artifact) return "";
  const content = normalizeMessageSignatureText(artifact.content);
  return [
    artifact.ref ?? "",
    artifact.kind,
    artifact.language ?? "",
    artifact.title,
    content,
    chartSpecSignature(artifact.chartSpec)
  ].join("|");
}

function messageSignature(message: ChatMessage): string {
  const type = message.type ?? "text";
  const content = normalizeMessageSignatureText(message.content);
  const chartHtml = normalizeMessageSignatureText(message.chartHtml ?? "");
  return `${message.role}|${type}|${content}|${chartSpecSignature(message.chartSpec)}|${chartHtml}|${artifactSignature(message.artifact)}`;
}

function messageCompletenessScore(message: ChatMessage): number {
  let score = 0;
  score += normalizeMessageSignatureText(message.content).length;
  if (typeof message.chartHtml === "string" && message.chartHtml.trim().length > 0) score += 500;
  if (message.chartSpec) score += 300 + message.chartSpec.data.length * 2;
  if (message.reasoning?.length) score += message.reasoning.join(" ").length;
  if (message.toolCalls?.length) score += message.toolCalls.length * 25;
  if (message.attachments?.length) score += message.attachments.length * 20;
  if (message.feedback) score += 5;
  if (message.artifact) score += normalizeMessageSignatureText(message.artifact.content).length + 250;
  return score;
}

function isLikelyDuplicateChatMessage(a: ChatMessage, b: ChatMessage): boolean {
  if (a.id === b.id) return true;
  if (a.role !== b.role) return false;
  if ((a.type ?? "text") !== (b.type ?? "text")) return false;
  if (messageSignature(a) !== messageSignature(b)) return false;

  const aTime = parseIsoTimestampMs(a.createdAt);
  const bTime = parseIsoTimestampMs(b.createdAt);
  if (aTime === null || bTime === null) return true;
  return Math.abs(aTime - bTime) <= DUPLICATE_MESSAGE_WINDOW_MS;
}

function dedupeChatMessages(messages: ChatMessage[]): ChatMessage[] {
  const deduped: ChatMessage[] = [];
  for (const candidate of messages) {
    const existingIndex = deduped.findIndex((message) => isLikelyDuplicateChatMessage(message, candidate));
    if (existingIndex < 0) {
      deduped.push(candidate);
      continue;
    }
    const existing = deduped[existingIndex];
    deduped[existingIndex] =
      messageCompletenessScore(candidate) >= messageCompletenessScore(existing) ? candidate : existing;
  }
  return deduped;
}

function readPendingAsyncChatJobs(): Record<string, PendingAsyncChatJob> {
  if (typeof window === "undefined") return {};
  const raw = window.localStorage.getItem(ASYNC_CHAT_PENDING_STORAGE_KEY);
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    if (!isRecord(parsed)) return {};
    const jobs: Record<string, PendingAsyncChatJob> = {};
    for (const [sessionKey, value] of Object.entries(parsed)) {
      if (!isRecord(value)) continue;
      const jobId = typeof value.jobId === "string" ? value.jobId.trim() : "";
      const sessionId =
        typeof value.sessionId === "string" && value.sessionId.trim().length > 0
          ? normalizeSessionId(value.sessionId)
          : normalizeSessionId(sessionKey);
      if (!jobId || !sessionId) continue;
      jobs[sessionId] = {
        jobId,
        sessionId,
        createdAtMs: typeof value.createdAtMs === "number" && Number.isFinite(value.createdAtMs) ? value.createdAtMs : Date.now(),
        updatedAtMs: typeof value.updatedAtMs === "number" && Number.isFinite(value.updatedAtMs) ? value.updatedAtMs : Date.now(),
        userMessage: isRecord(value.userMessage) ? value.userMessage : undefined
      };
    }
    return jobs;
  } catch {
    return {};
  }
}

function writePendingAsyncChatJobs(jobs: Record<string, PendingAsyncChatJob>): void {
  if (typeof window === "undefined") return;
  try {
    const entries = Object.entries(jobs);
    if (!entries.length) {
      window.localStorage.removeItem(ASYNC_CHAT_PENDING_STORAGE_KEY);
      return;
    }
    window.localStorage.setItem(ASYNC_CHAT_PENDING_STORAGE_KEY, JSON.stringify(jobs));
  } catch {
    // Best-effort cache only.
  }
}

function getPendingAsyncChatJob(sessionId: string): PendingAsyncChatJob | null {
  const normalizedSessionId = normalizeSessionId(sessionId);
  const jobs = readPendingAsyncChatJobs();
  return jobs[normalizedSessionId] ?? null;
}

function setPendingAsyncChatJob(job: PendingAsyncChatJob): void {
  const jobs = readPendingAsyncChatJobs();
  jobs[job.sessionId] = {
    ...job,
    sessionId: normalizeSessionId(job.sessionId),
    updatedAtMs: Date.now()
  };
  writePendingAsyncChatJobs(jobs);
}

function clearPendingAsyncChatJob(sessionId: string, jobId?: string): void {
  const normalizedSessionId = normalizeSessionId(sessionId);
  const jobs = readPendingAsyncChatJobs();
  const existing = jobs[normalizedSessionId];
  if (!existing) return;
  if (typeof jobId === "string" && jobId.trim().length > 0 && existing.jobId !== jobId.trim()) {
    return;
  }
  delete jobs[normalizedSessionId];
  writePendingAsyncChatJobs(jobs);
}

function mapPendingUserMessage(sessionId: string, payload: Record<string, unknown>): ChatMessage | null {
  const mapped = mapHistoryMessage(sessionId, payload, 0);
  if (mapped.role !== "user") return null;
  return mapped;
}

function toUploadedFile(sessionId: string, fileReference: BackendFileReference): UploadedFile {
  const name = fileReference.original_name || fileReference.display_name || fileReference.name || "attachment";
  return {
    id: fileReference.reference_id || fileReference.name || makeId("file"),
    sessionId,
    name,
    sizeLabel: formatFileSize(fileReference.size_bytes),
    createdAt: normalizeDate(fileReference.upload_time),
    localPath: typeof fileReference.local_path === "string" ? fileReference.local_path : undefined
  };
}

function mapRitual(payload: unknown): Ritual {
  const raw = (payload ?? {}) as Record<string, unknown>;
  const scheduleConfig = (raw.schedule_config ?? raw.schedule) as Record<string, unknown> | undefined;
  const scheduleTimeRaw = scheduleConfig?.schedule_time ?? scheduleConfig?.time;
  const frequencyHoursRaw = scheduleConfig?.frequency_hours ?? scheduleConfig?.frequencyHours;
  const parsedFrequencyHours =
    typeof frequencyHoursRaw === "number"
      ? frequencyHoursRaw
      : typeof frequencyHoursRaw === "string"
        ? Number(frequencyHoursRaw)
        : undefined;
  const endTimeRaw = scheduleConfig?.end_time ?? scheduleConfig?.endTime;
  return {
    id: typeof raw.id === "string" ? raw.id : makeId("ritual"),
    name: typeof raw.name === "string" ? raw.name : "Untitled ritual",
    query: typeof raw.query === "string" ? raw.query : "",
    schedule: {
      enabled: scheduleConfig?.enabled === true,
      timezone: typeof scheduleConfig?.timezone === "string" ? scheduleConfig.timezone : "America/New_York",
      days: Array.isArray(scheduleConfig?.days) ? scheduleConfig.days.filter((item): item is string => typeof item === "string") : [],
      time: typeof scheduleTimeRaw === "string" ? scheduleTimeRaw : "08:00",
      frequencyHours:
        typeof parsedFrequencyHours === "number" && Number.isFinite(parsedFrequencyHours)
          ? parsedFrequencyHours
          : undefined,
      endTime: typeof endTimeRaw === "string" ? endTimeRaw : undefined
    },
    updatedAt: normalizeDate(raw.updated_at ?? raw.created_at ?? raw.updatedAt)
  };
}

function toBackendRitualScheduleConfig(schedule: Ritual["schedule"] | undefined): Record<string, unknown> | undefined {
  if (!schedule) return undefined;
  return {
    type: "weekly",
    enabled: schedule.enabled,
    timezone: schedule.timezone,
    days: schedule.days,
    time: schedule.time,
    schedule_time: schedule.time,
    frequency_hours: schedule.frequencyHours ?? 24,
    ...(schedule.endTime ? { end_time: schedule.endTime } : {})
  };
}

function mapWorkflowSummary(payload: unknown): WorkflowSummary {
  const raw = (payload ?? {}) as Record<string, unknown>;
  const status = raw.status === "active" || raw.status === "archived" ? raw.status : "draft";
  return {
    id: typeof raw.id === "string" ? raw.id : makeId("wf"),
    name: typeof raw.name === "string" ? raw.name : "Untitled workflow",
    description: typeof raw.description === "string" ? raw.description : "No description provided",
    status,
    updatedAt: normalizeDate(raw.updated_at ?? raw.created_at ?? raw.updatedAt)
  };
}

function mapAgentSummary(payload: unknown): AgentSummary {
  const raw = (payload ?? {}) as Record<string, unknown>;
  const name = typeof raw.name === "string" && raw.name.trim().length > 0 ? raw.name.trim() : "stella";
  const displayName =
    typeof raw.display_name === "string" && raw.display_name.trim().length > 0
      ? raw.display_name.trim()
      : typeof raw.displayName === "string" && raw.displayName.trim().length > 0
        ? raw.displayName.trim()
        : name;
  return {
    name,
    displayName,
    description: typeof raw.description === "string" && raw.description.trim().length > 0 ? raw.description.trim() : undefined
  };
}

function formatWorkflowJson(payload: unknown): string {
  if (typeof payload === "string") return payload;
  if (payload && typeof payload === "object") return JSON.stringify(payload, null, 2);
  return "{\n  \"steps\": []\n}";
}

function readStoredToken(): string | null {
  if (typeof window === "undefined") return null;
  const raw = window.localStorage.getItem(AUTH_STORAGE_KEY);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as { state?: { token?: unknown } };
    return typeof parsed.state?.token === "string" && parsed.state.token ? parsed.state.token : null;
  } catch {
    return null;
  }
}

function getErrorMessage(payload: unknown, fallback: string): string {
  if (typeof payload === "string" && payload.trim()) return payload;
  if (payload && typeof payload === "object") {
    const raw = payload as Record<string, unknown>;
    if (typeof raw.detail === "string" && raw.detail) return raw.detail;
    if (typeof raw.message === "string" && raw.message) return raw.message;
    if (typeof raw.error === "string" && raw.error) return raw.error;
  }
  return fallback;
}

function buildReplyAwareInput(
  content: string,
  replyTo?: {
    messageId: string;
    excerpt: string;
  }
): string {
  const excerpt = replyTo?.excerpt?.trim();
  if (!excerpt) return content;
  const reply = content.trim();
  return [
    "User reply to your earlier text.",
    `Earlier Text: ${excerpt}`,
    `Reply: ${reply || "(no additional text)"}`
  ].join("\n");
}

function waitFor(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

function isTransientRequestError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const message = error.message.toLowerCase();
  return (
    message.includes("socket hang up") ||
    message.includes("econnreset") ||
    message.includes("fetch failed") ||
    message.includes("failed to fetch") ||
    message.includes("networkerror") ||
    message.includes("request failed (500)") ||
    message.includes("request failed (502)") ||
    message.includes("request failed (503)") ||
    message.includes("request failed (504)") ||
    message.includes("internal server error") ||
    message.includes("bad gateway") ||
    message.includes("gateway timeout") ||
    message.includes("service unavailable")
  );
}

function isProxyDisconnectError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const message = error.message.toLowerCase();
  return message.includes("socket hang up") || message.includes("econnreset");
}

async function sendChatMessageWithRetry(payload: {
  session_id: string;
  user_input: string;
  file_refs?: BackendFileReference[];
  agent_name?: string;
  web_search?: boolean;
}, options?: { allowProxyDisconnectRetry?: boolean }): Promise<ChatMessageApiResponse> {
  const retryDelaysMs = [350, 900];
  const allowProxyDisconnectRetry = options?.allowProxyDisconnectRetry === true;
  let lastError: unknown;
  for (let attempt = 0; attempt <= retryDelaysMs.length; attempt += 1) {
    try {
      return await apiRequest<ChatMessageApiResponse>("/api/chat/message", {
        method: "POST",
        body: payload
      });
    } catch (error) {
      lastError = error;
      const shouldRetry = (
        attempt < retryDelaysMs.length &&
        isTransientRequestError(error) &&
        (allowProxyDisconnectRetry || !isProxyDisconnectError(error))
      );
      if (!shouldRetry) {
        throw error;
      }
      await waitFor(retryDelaysMs[attempt]);
    }
  }
  throw lastError instanceof Error ? lastError : new Error("Failed to generate response");
}

const ASYNC_CHAT_POLL_INTERVAL_MS = Number(process.env.NEXT_PUBLIC_CHAT_ASYNC_POLL_INTERVAL_MS ?? "2000");
const ASYNC_CHAT_CLIENT_TIMEOUT_MS = Number(process.env.NEXT_PUBLIC_CHAT_ASYNC_CLIENT_TIMEOUT_MS ?? "21600000");
const ASYNC_CHAT_MAX_RUNTIME_SECONDS = Number(process.env.NEXT_PUBLIC_CHAT_ASYNC_MAX_RUNTIME_SECONDS ?? "21600");
let asyncChatApiAvailability: "unknown" | "available" | "unavailable" = "unknown";
const MAX_HISTORY_SAVE_PAYLOAD_CHARS = Number(process.env.NEXT_PUBLIC_HISTORY_SAVE_MAX_PAYLOAD_CHARS ?? "750000");
const MAX_PERSISTED_CHART_HTML_CHARS = Number(process.env.NEXT_PUBLIC_PERSISTED_CHART_HTML_CHARS ?? "20000");
const MIN_HISTORY_ENTRIES_TO_KEEP = Number(process.env.NEXT_PUBLIC_MIN_HISTORY_ENTRIES_TO_KEEP ?? "8");

function isAsyncStartUnavailableError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const message = error.message.toLowerCase();
  return (
    message.includes("request failed (404)") ||
    message.includes("not found") ||
    message.includes("/api/chat/message/async/start")
  );
}

function mapAsyncProgressMessages(progress: unknown): string[] {
  if (!Array.isArray(progress)) return [];
  return progress
    .map((item) => {
      if (!isRecord(item)) return null;
      const message = typeof item.message === "string" ? item.message.trim() : "";
      return message || null;
    })
    .filter((item): item is string => item !== null);
}

async function sendChatMessageWithAsyncPolling(payload: {
  session_id: string;
  user_input: string;
  file_refs?: BackendFileReference[];
  agent_name?: string;
  web_search?: boolean;
}, userMessageForRecovery?: ChatMessage, onProgress?: (messages: string[]) => void): Promise<ChatMessageApiResponse> {
  const normalizedSessionId = normalizeSessionId(payload.session_id);
  const startResponse = await apiRequest<AsyncChatStartResponse>("/api/chat/message/async/start", {
    method: "POST",
    body: {
      ...payload,
      max_runtime_seconds: ASYNC_CHAT_MAX_RUNTIME_SECONDS
    }
  });

  if (!startResponse.success || typeof startResponse.job_id !== "string" || !startResponse.job_id.trim()) {
    throw new Error("Failed to start async chat task");
  }
  asyncChatApiAvailability = "available";

  const jobId = startResponse.job_id.trim();
  setPendingAsyncChatJob({
    jobId,
    sessionId: normalizedSessionId,
    createdAtMs: Date.now(),
    updatedAtMs: Date.now(),
    userMessage: userMessageForRecovery ? toPersistedHistoryMessage(userMessageForRecovery) : undefined
  });

  if (userMessageForRecovery) {
    try {
      await persistChatHistoryWithRetry(normalizedSessionId, [toPersistedHistoryMessage(userMessageForRecovery)]);
    } catch {
      // Best-effort; final history persistence runs after completion too.
    }
  }

  return pollAsyncChatJobUntilComplete(
    normalizedSessionId,
    jobId,
    Date.now() + ASYNC_CHAT_CLIENT_TIMEOUT_MS,
    onProgress
  );
}

async function pollAsyncChatJobUntilComplete(
  normalizedSessionId: string,
  jobId: string,
  deadlineMs: number,
  onProgress?: (messages: string[]) => void
): Promise<ChatMessageApiResponse> {
  while (Date.now() < deadlineMs) {
    const statusResponse = await apiRequest<AsyncChatStatusResponse>(
      `/api/chat/message/async/${encodeURIComponent(jobId)}`
    );

    if (onProgress) {
      onProgress(mapAsyncProgressMessages(statusResponse.progress));
    }

    if (!statusResponse.success) {
      throw new Error(statusResponse.error || "Async chat task failed");
    }

    if (statusResponse.status === "completed") {
      clearPendingAsyncChatJob(normalizedSessionId, jobId);
      if (statusResponse.result) {
        return statusResponse.result;
      }
      throw new Error("Async chat task completed without result payload");
    }

    if (statusResponse.status === "failed") {
      clearPendingAsyncChatJob(normalizedSessionId, jobId);
      throw new Error(statusResponse.error || statusResponse.result?.error || "Async chat task failed");
    }

    await waitFor(ASYNC_CHAT_POLL_INTERVAL_MS);
  }

  throw new Error("Async chat task exceeded client wait timeout");
}

async function resumePendingAsyncChatMessage(sessionId: string): Promise<{
  response: ChatMessageApiResponse;
  pendingUserMessage?: ChatMessage;
} | null> {
  const normalizedSessionId = normalizeSessionId(sessionId);
  const pending = getPendingAsyncChatJob(normalizedSessionId);
  if (!pending) return null;

  try {
    const response = await pollAsyncChatJobUntilComplete(
      normalizedSessionId,
      pending.jobId,
      Date.now() + ASYNC_CHAT_CLIENT_TIMEOUT_MS
    );
    const pendingUserMessage = pending.userMessage ? mapPendingUserMessage(normalizedSessionId, pending.userMessage) : null;
    return {
      response,
      pendingUserMessage: pendingUserMessage ?? undefined
    };
  } catch (error) {
    if (error instanceof Error && error.message.toLowerCase().includes("request failed (404)")) {
      clearPendingAsyncChatJob(normalizedSessionId, pending.jobId);
    }
    throw error;
  }
}

function hasPendingAsyncChatMessage(sessionId: string): boolean {
  return getPendingAsyncChatJob(sessionId) !== null;
}

function normalizeHistoryTimestampBucket(value: unknown): string {
  if (typeof value !== "string" || !value.trim()) return "";
  const parsed = Date.parse(value);
  if (!Number.isFinite(parsed)) return value.trim();
  return String(Math.floor(parsed / 1000));
}

function normalizeHistoryEntryText(value: unknown): string {
  return typeof value === "string" ? value.trim().replace(/\s+/g, " ") : "";
}

function historyEntryId(entry: Record<string, unknown>): string {
  return typeof entry.id === "string" ? entry.id.trim() : "";
}

function historyEntryFingerprint(entry: Record<string, unknown>): string {
  const role = typeof entry.role === "string" ? entry.role : "";
  const type = typeof entry.type === "string" ? entry.type : "";
  const content = normalizeHistoryEntryText(entry.content);
  const data = normalizeHistoryEntryText(entry.data);
  const chartSpecId =
    isRecord(entry.chart_spec) && typeof entry.chart_spec.id === "string"
      ? entry.chart_spec.id
      : "";
  const artifact =
    isRecord(entry.artifact) ? entry.artifact : isRecord(entry.artifact_payload) ? entry.artifact_payload : null;
  const artifactRef = artifact && typeof artifact.ref === "string" ? artifact.ref : "";
  const artifactTitle = artifact && typeof artifact.title === "string" ? artifact.title : "";
  const artifactKind = artifact && typeof artifact.kind === "string" ? artifact.kind : "";
  const artifactContent = artifact ? normalizeHistoryEntryText(artifact.content) : "";
  const createdAtBucket = normalizeHistoryTimestampBucket(entry.created_at ?? entry.createdAt);
  return `${role}|${type}|${content}|${data}|${chartSpecId}|${artifactRef}|${artifactTitle}|${artifactKind}|${artifactContent}|${createdAtBucket}`;
}

function mergeHistoryEntries(
  existingEntries: Record<string, unknown>[],
  newEntries: Record<string, unknown>[]
): Record<string, unknown>[] {
  const merged: Record<string, unknown>[] = [];
  const seenIds = new Set<string>();
  const seenFingerprints = new Set<string>();

  const addEntry = (entry: Record<string, unknown>): void => {
    const id = historyEntryId(entry);
    const fingerprint = historyEntryFingerprint(entry);
    if (id && seenIds.has(id)) return;
    if (fingerprint && seenFingerprints.has(fingerprint)) return;
    if (id) seenIds.add(id);
    if (fingerprint) seenFingerprints.add(fingerprint);
    merged.push(entry);
  };

  existingEntries.forEach(addEntry);
  newEntries.forEach(addEntry);
  return merged;
}

function normalizeChartEntryForPersistence(entry: Record<string, unknown>): Record<string, unknown> {
  if (entry.type !== "image" || typeof entry.data !== "string") {
    return entry;
  }

  const chartHtml = entry.data.trim();
  if (!chartHtml) return entry;

  const chartRef = extractChartReferenceFromHtml(chartHtml);
  if (chartRef !== null) {
    return {
      ...entry,
      data: buildChartReferenceHtml(chartRef),
      mime_type: "image/html"
    };
  }

  if (chartHtml.length <= MAX_PERSISTED_CHART_HTML_CHARS) {
    return entry;
  }

  const downgraded = { ...entry };
  delete downgraded.type;
  delete downgraded.data;
  delete downgraded.mime_type;
  if (typeof downgraded.content !== "string" || !downgraded.content.trim()) {
    downgraded.content = "Chart generated. Open this conversation in-session to render it again.";
  }
  return downgraded;
}

function prepareHistoryPayload(entries: Record<string, unknown>[]): Record<string, unknown>[] {
  const normalizedEntries = entries.map((entry) => normalizeChartEntryForPersistence(entry));
  let payload = normalizedEntries;
  let serializedSize = JSON.stringify(payload).length;

  while (serializedSize > MAX_HISTORY_SAVE_PAYLOAD_CHARS && payload.length > MIN_HISTORY_ENTRIES_TO_KEEP) {
    payload = payload.slice(1);
    serializedSize = JSON.stringify(payload).length;
  }

  return payload;
}

async function persistChatHistoryWithRetry(
  normalizedSessionId: string,
  newEntries: Record<string, unknown>[]
): Promise<void> {
  const retryDelaysMs = [450, 1100];
  let lastError: unknown;

  for (let attempt = 0; attempt <= retryDelaysMs.length; attempt += 1) {
    try {
      const existingHistoryResponse = await apiRequest<{ success: boolean; history?: unknown[] }>(
        `/api/sessions/${encodeURIComponent(normalizedSessionId)}/history`
      );
      const existingHistory = existingHistoryResponse.success && Array.isArray(existingHistoryResponse.history)
        ? existingHistoryResponse.history.filter(isRecord)
        : [];
      const updatedHistory = mergeHistoryEntries(existingHistory, newEntries);
      const payloadHistory = prepareHistoryPayload(updatedHistory);

      await apiRequest<{ success: boolean; message?: string }>(
        `/api/chat/save-history?session_id=${encodeURIComponent(normalizedSessionId)}`,
        {
          method: "POST",
          body: payloadHistory
        }
      );
      return;
    } catch (error) {
      lastError = error;
      const shouldRetry = attempt < retryDelaysMs.length && isTransientRequestError(error);
      if (!shouldRetry) break;
      await waitFor(retryDelaysMs[attempt]);
    }
  }

  throw lastError instanceof Error ? lastError : new Error("Failed to persist chat history");
}

async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const url = `${getApiBaseUrl()}${path}`;
  const method = options.method ?? "GET";
  const useAuth = options.auth !== false;
  const token = useAuth ? (options.authToken ?? readStoredToken()) : null;

  const headers: Record<string, string> = {
    Accept: "application/json",
    ...(options.headers ?? {})
  };

  let body: BodyInit | undefined;
  if (options.body instanceof FormData) {
    body = options.body;
  } else if (options.body !== undefined) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(options.body);
  }

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    method,
    headers,
    body
  });

  const contentType = response.headers.get("content-type") ?? "";
  const payload = contentType.includes("application/json") ? await response.json().catch(() => null) : await response.text().catch(() => null);

  if (!response.ok) {
    throw new Error(getErrorMessage(payload, `Request failed (${response.status})`));
  }

  return payload as T;
}

async function apiRequestBlob(path: string, options: RequestOptions = {}): Promise<Blob> {
  const url = `${getApiBaseUrl()}${path}`;
  const method = options.method ?? "GET";
  const useAuth = options.auth !== false;
  const token = useAuth ? (options.authToken ?? readStoredToken()) : null;
  const headers: Record<string, string> = {
    ...(options.headers ?? {})
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    method,
    headers
  });

  if (!response.ok) {
    const contentType = response.headers.get("content-type") ?? "";
    const payload = contentType.includes("application/json")
      ? await response.json().catch(() => null)
      : await response.text().catch(() => null);
    throw new Error(getErrorMessage(payload, `Request failed (${response.status})`));
  }

  return response.blob();
}

const agentService: AgentService = {
  async listAgents(): Promise<AgentSummary[]> {
    const result = await apiRequest<{ success: boolean; agents?: unknown[] }>("/api/agents/list");
    if (!result.success) {
      throw new Error("Unable to load agents");
    }
    return (result.agents ?? []).map(mapAgentSummary);
  }
};

const authService: AuthService = {
  async signIn(username: string, password: string): Promise<AuthSession> {
    const result = await apiRequest<{
      success: boolean;
      message: string;
      token?: string;
      user_info?: { username?: string; is_admin?: boolean };
    }>("/api/auth/login", {
      method: "POST",
      auth: false,
      body: { username, password }
    });

    if (!result.success || !result.token) {
      throw new Error(result.message || "Unable to sign in");
    }

    let user: User | null = null;
    try {
      const profileResponse = await apiRequest<{ success: boolean; user?: unknown }>("/api/profile/me", {
        authToken: result.token
      });
      if (profileResponse.success && profileResponse.user) {
        user = mapUser(profileResponse.user);
      }
    } catch {
      user = null;
    }

    if (!user) {
      user = {
        username: result.user_info?.username || username,
        email: "",
        department: undefined,
        analyst: undefined,
        role: result.user_info?.is_admin ? "admin" : "user",
        status: "approved",
        createdAt: new Date().toISOString()
      };
    }

    return {
      token: result.token,
      user
    };
  },

  async signUp(input): Promise<{ status: "pending" | "approved"; message: string }> {
    const result = await apiRequest<{ success: boolean; message: string }>("/api/auth/register", {
      method: "POST",
      auth: false,
      body: input
    });

    if (!result.success) {
      throw new Error(result.message || "Unable to register");
    }

    return { status: "pending", message: result.message || "Account created successfully." };
  },

  async listRegistrationAnalysts(): Promise<string[]> {
    const result = await apiRequest<{ success: boolean; analysts?: unknown[]; message?: string }>("/api/auth/analysts", {
      auth: false
    });
    if (!result.success) {
      throw new Error(result.message || "Unable to load analysts");
    }
    return (result.analysts ?? []).filter((item): item is string => typeof item === "string" && item.trim().length > 0);
  },

  async verifyEmail(token: string): Promise<{ message: string }> {
    const result = await apiRequest<{ success: boolean; message: string }>("/api/auth/verify-email", {
      method: "POST",
      auth: false,
      body: { token }
    });
    if (!result.success) {
      throw new Error(result.message || "Unable to verify email");
    }
    return { message: result.message || "Email verified successfully." };
  },

  async resendVerification(identifier: string): Promise<{ message: string }> {
    const result = await apiRequest<{ success: boolean; message: string }>("/api/auth/resend-verification", {
      method: "POST",
      auth: false,
      body: { identifier }
    });
    if (!result.success) {
      throw new Error(result.message || "Unable to resend verification email");
    }
    return { message: result.message || "Verification email sent." };
  },

  async forgotPassword(identifier: string): Promise<{ message: string }> {
    const result = await apiRequest<{ success: boolean; message: string }>("/api/auth/forgot-password", {
      method: "POST",
      auth: false,
      body: { identifier }
    });
    if (!result.success) {
      throw new Error(result.message || "Unable to process forgot password request");
    }
    return {
      message: result.message || "If an account exists for that username/email, a password reset link has been sent."
    };
  },

  async resetPassword(token: string, newPassword: string): Promise<{ message: string }> {
    const result = await apiRequest<{ success: boolean; message: string }>("/api/auth/reset-password", {
      method: "POST",
      auth: false,
      body: { token, new_password: newPassword }
    });
    if (!result.success) {
      throw new Error(result.message || "Unable to reset password");
    }
    return { message: result.message || "Password has been reset successfully." };
  },

  async me(token: string): Promise<User> {
    const profileResponse = await apiRequest<{ success: boolean; user?: unknown }>("/api/profile/me", {
      authToken: token
    });
    if (!profileResponse.success || !profileResponse.user) {
      throw new Error("Unauthorized");
    }
    return mapUser(profileResponse.user);
  },

  async signOut(): Promise<void> {
    try {
      await apiRequest("/api/auth/logout", { method: "POST" });
    } catch {
      // No-op on sign-out failure to keep UX smooth.
    }
  }
};

const sessionService: SessionService = {
  async listSessions(): Promise<ChatSession[]> {
    const result = await apiRequest<{ success: boolean; chats?: Record<string, string> }>("/api/sessions/list");
    if (!result.success) {
      throw new Error("Failed to load chat sessions");
    }
    const baseMs = Date.now();
    const dedupedSessions = new Map<string, ChatSession>();
    // Backend returns newest first; preserve order (do not reverse)
    const entries = Object.entries(result.chats ?? {});
    for (const [index, [rawId, title]] of entries.entries()) {
      const id = normalizeSessionId(rawId);
      if (!id) continue;
      // First entry (index 0) is newest, so it gets highest timestamp
      const orderedTimestamp = new Date(baseMs - index).toISOString();

      const nextSession: ChatSession = {
        id,
        title,
        createdAt: orderedTimestamp,
        updatedAt: orderedTimestamp
      };

      const existing = dedupedSessions.get(id);
      if (!existing) {
        dedupedSessions.set(id, nextSession);
        continue;
      }

      // Keep the more informative title when duplicate encoded/decoded ids appear.
      if (existing.title === "New chat" && title !== "New chat") {
        dedupedSessions.set(id, nextSession);
      }
    }

    return Array.from(dedupedSessions.values());
  },

  async createSession(_username: string, options?: { agentName?: string }): Promise<ChatSession> {
    const result = await apiRequest<{
      success: boolean;
      session_id: string;
      created_at?: string;
      agent_name?: string;
    }>("/api/sessions/create", {
      method: "POST",
      body: {
        agent_name: options?.agentName
      }
    });

    if (!result.success || !result.session_id) {
      throw new Error("Unable to create session");
    }
    const now = new Date().toISOString();

    return {
      id: normalizeSessionId(result.session_id),
      title: "New chat",
      // Ensure brand-new chats sort to the top immediately in the sidebar.
      createdAt: now,
      updatedAt: now
    };
  },

  async renameSession(id: string, title: string): Promise<void> {
    const sessionId = normalizeSessionId(id);
    const result = await apiRequest<{ success: boolean; message?: string }>(`/api/sessions/${encodeURIComponent(sessionId)}/rename`, {
      method: "POST",
      body: {
        session_id: sessionId,
        new_title: title
      }
    });

    if (!result.success) {
      throw new Error(result.message || "Unable to rename session");
    }
  },

  async deleteSession(id: string): Promise<void> {
    const sessionId = normalizeSessionId(id);
    const result = await apiRequest<{ success: boolean; message?: string }>(`/api/sessions/${encodeURIComponent(sessionId)}`, {
      method: "DELETE"
    });

    if (!result.success) {
      throw new Error(result.message || "Unable to delete session");
    }
  },

  async shareSession(id: string): Promise<string> {
    const sessionId = normalizeSessionId(id);
    const result = await apiRequest<{ success: boolean; uuid?: string; message?: string }>(
      `/api/sessions/${encodeURIComponent(sessionId)}/share`,
      { method: "POST" }
    );

    if (!result.success || !result.uuid) {
      throw new Error(result.message || "Unable to share session");
    }

    return result.uuid;
  },

  async getSessionByUuid(uuid: string): Promise<SharedSessionLookup> {
    const result = await apiRequest<{
      success: boolean;
      session_id?: string;
      owner?: string;
      is_shared?: boolean;
      is_owner?: boolean;
      message?: string;
    }>(`/api/sessions/uuid/${encodeURIComponent(uuid)}`);

    if (!result.success || !result.session_id || !result.owner) {
      throw new Error(result.message || "Unable to load shared conversation");
    }

    return {
      sessionId: normalizeSessionId(result.session_id),
      owner: result.owner,
      isShared: result.is_shared === true,
      isOwner: result.is_owner === true
    };
  },

  async addSharedConversation(sessionId: string): Promise<void> {
    const normalizedSessionId = normalizeSessionId(sessionId);
    const result = await apiRequest<{ success: boolean; message?: string }>(
      `/api/sessions/${encodeURIComponent(normalizedSessionId)}/add-shared`,
      { method: "POST" }
    );

    if (!result.success) {
      throw new Error(result.message || "Unable to add shared conversation");
    }
  },

  async exportSessionMarkdown(id: string): Promise<string> {
    const sessionId = normalizeSessionId(id);
    const result = await apiRequest<{ success: boolean; content?: string; message?: string }>(
      `/api/sessions/${encodeURIComponent(sessionId)}/export/markdown`
    );
    if (!result.success || typeof result.content !== "string") {
      throw new Error(result.message || "Unable to export markdown");
    }
    return result.content;
  },

  async exportSessionPdf(id: string): Promise<Blob> {
    const sessionId = normalizeSessionId(id);
    return apiRequestBlob(`/api/sessions/${encodeURIComponent(sessionId)}/export/pdf`);
  }
};

const chatService: ChatService = {
  async getHistory(sessionId: string): Promise<ChatMessage[]> {
    const normalizedSessionId = normalizeSessionId(sessionId);
    const result = await apiRequest<{ success: boolean; history?: unknown[] }>(
      `/api/sessions/${encodeURIComponent(normalizedSessionId)}/history`
    );
    if (!result.success) {
      throw new Error("Unable to load chat history");
    }
    const mapped = (result.history ?? [])
      .map((message, index) => mapHistoryMessage(normalizedSessionId, message, index))
      .filter((message) => !(message.role === "assistant" && message.type !== "chart" && !message.content.trim().length));
    return dedupeChatMessages(mapped);
  },

  async getChartHtml(chartRef: string): Promise<string> {
    const normalizedRef = chartRef.trim();
    if (!normalizedRef) {
      throw new Error("Chart reference is required");
    }
    const result = await apiRequest<ChartHtmlApiResponse>(`/api/charts/${encodeURIComponent(normalizedRef)}`);
    if (!result.success || typeof result.html !== "string" || !result.html.trim()) {
      throw new Error(result.error || "Unable to load chart");
    }
    return result.html;
  },

  async saveHistory(sessionId: string, messages: ChatMessage[]): Promise<void> {
    const normalizedSessionId = normalizeSessionId(sessionId);
    const payload = prepareHistoryPayload(messages.map((message) => toPersistedHistoryMessage(message)));
    const result = await apiRequest<{ success: boolean; message?: string }>(
      `/api/chat/save-history?session_id=${encodeURIComponent(normalizedSessionId)}`,
      {
        method: "POST",
        body: payload
      }
    );

    if (!result.success) {
      throw new Error(result.message || "Unable to save chat history");
    }
  },

  hasPendingMessage(sessionId: string): boolean {
    return hasPendingAsyncChatMessage(sessionId);
  },

  async resumePendingMessage(sessionId: string): Promise<ChatMessage[] | null> {
    const normalizedSessionId = normalizeSessionId(sessionId);
    const pendingResult = await resumePendingAsyncChatMessage(normalizedSessionId);
    if (!pendingResult) return null;

    const response = pendingResult.response;
    if (!response.success || !response.response_data) {
      throw new Error(response.error || "Failed to resume pending response");
    }

    const assistantMessages = buildAssistantMessagesFromResponse(normalizedSessionId, response.response_data);
    const persistedEntries: Record<string, unknown>[] = [
      ...(pendingResult.pendingUserMessage ? [toPersistedHistoryMessage(pendingResult.pendingUserMessage)] : []),
      ...assistantMessages.map((message) => toPersistedHistoryMessage(message))
    ];

    if (persistedEntries.length) {
      try {
        await persistChatHistoryWithRetry(normalizedSessionId, persistedEntries);
      } catch {
        // Keep response usable even if persistence fails.
      }
    }

    const pendingAndAssistant = pendingResult.pendingUserMessage
      ? [pendingResult.pendingUserMessage, ...assistantMessages]
      : assistantMessages;
    return dedupeChatMessages(pendingAndAssistant);
  },

  async sendMessage(
    sessionId: string,
    content: string,
    options?: {
      replyTo?: {
        messageId: string;
        excerpt: string;
      };
      attachments?: Attachment[];
      agentName?: string;
      skillId?: string;
      webSearch?: boolean;
      onProgress?: (messages: string[]) => void;
    }
  ): Promise<ChatMessage[]> {
    const normalizedSessionId = normalizeSessionId(sessionId);
    const modelInput = buildReplyAwareInput(content, options?.replyTo);

    let fileReferences: BackendFileReference[] | undefined;
    try {
      const filesResult = await apiRequest<{ success: boolean; files?: BackendFileReference[] }>(
        `/api/files/session/${encodeURIComponent(normalizedSessionId)}`
      );
      if (filesResult.success && Array.isArray(filesResult.files) && filesResult.files.length) {
        fileReferences = filesResult.files;
      }
    } catch {
      fileReferences = undefined;
    }

    const requestPayload = {
      session_id: normalizedSessionId,
      user_input: modelInput,
      file_refs: fileReferences,
      agent_name: options?.agentName,
      skill_id: cleanOptionalText(options?.skillId),
      web_search: options?.webSearch === true
    };

    const userMessage: ChatMessage = {
      id: makeId("msg"),
      sessionId: normalizedSessionId,
      role: "user",
      content,
      createdAt: new Date().toISOString(),
      replyTo: options?.replyTo,
      attachments: options?.attachments
    };

    let response: ChatMessageApiResponse;
    if (asyncChatApiAvailability !== "unavailable") {
      try {
        response = await sendChatMessageWithAsyncPolling(requestPayload, userMessage, options?.onProgress);
      } catch (error) {
        if (!isAsyncStartUnavailableError(error)) {
          throw error;
        }
        asyncChatApiAvailability = "unavailable";
        options?.onProgress?.([]);
        response = await sendChatMessageWithRetry(requestPayload, { allowProxyDisconnectRetry: false });
      }
    } else {
      options?.onProgress?.([]);
      response = await sendChatMessageWithRetry(requestPayload, { allowProxyDisconnectRetry: false });
    }

    if (!response.success || !response.response_data) {
      throw new Error(response.error || "Failed to generate response");
    }

    const assistantMessages = buildAssistantMessagesFromResponse(normalizedSessionId, response.response_data);

    try {
      await persistChatHistoryWithRetry(normalizedSessionId, [
        toPersistedHistoryMessage(userMessage),
        ...assistantMessages.map((message) => toPersistedHistoryMessage(message))
      ]);
    } catch {
      // Keep response usable even if persistence fails.
    }

    return dedupeChatMessages([userMessage, ...assistantMessages]);
  }
};

const ritualService: RitualService = {
  async listRituals(): Promise<Ritual[]> {
    const result = await apiRequest<{ success: boolean; rituals?: unknown[] }>("/api/rituals/list");
    if (!result.success) {
      throw new Error("Unable to load rituals");
    }
    return (result.rituals ?? []).map(mapRitual);
  },

  async upsertRitual(_username: string, ritual): Promise<Ritual> {
    if (ritual.id) {
      const updateResult = await apiRequest<{ success: boolean; message?: string; ritual?: unknown }>("/api/rituals/update", {
        method: "PUT",
        body: {
          ritual_id: ritual.id,
          name: ritual.name,
          query: ritual.query
        }
      });

      if (!updateResult.success) {
        throw new Error(updateResult.message || "Unable to update ritual");
      }

      let updatedRitualPayload = updateResult.ritual;

      if (ritual.schedule) {
        const scheduleResult = await apiRequest<{ success: boolean; message?: string; ritual?: unknown }>("/api/rituals/schedule", {
          method: "PUT",
          body: {
            ritual_id: ritual.id,
            schedule_config: toBackendRitualScheduleConfig(ritual.schedule)
          }
        });
        if (!scheduleResult.success) {
          throw new Error(scheduleResult.message || "Unable to update ritual schedule");
        }
        updatedRitualPayload = scheduleResult.ritual ?? updatedRitualPayload;
      }

      if (updatedRitualPayload) return mapRitual(updatedRitualPayload);

      const byId = await apiRequest<{ success: boolean; ritual?: unknown; message?: string }>(
        `/api/rituals/${encodeURIComponent(ritual.id)}`
      );
      if (byId.success && byId.ritual) return mapRitual(byId.ritual);
    } else {
      const createResult = await apiRequest<{ success: boolean; message?: string; ritual?: unknown }>("/api/rituals/create", {
        method: "POST",
        body: {
          name: ritual.name,
          query: ritual.query,
          schedule_config: toBackendRitualScheduleConfig(ritual.schedule)
        }
      });

      if (!createResult.success) {
        throw new Error(createResult.message || "Unable to create ritual");
      }
      if (createResult.ritual) return mapRitual(createResult.ritual);
    }

    const rituals = await ritualService.listRituals(_username);
    const matched = rituals.find((item) => item.name === ritual.name && item.query === ritual.query);
    return (
      matched ?? {
        id: ritual.id ?? makeId("ritual"),
        name: ritual.name,
        query: ritual.query,
        schedule: ritual.schedule ?? {
          enabled: false,
          timezone: "America/New_York",
          days: [],
          time: "08:00"
        },
        updatedAt: new Date().toISOString()
      }
    );
  },

  async deleteRitual(_username: string, id: string): Promise<void> {
    const result = await apiRequest<{ success: boolean; message?: string }>(`/api/rituals/${encodeURIComponent(id)}`, {
      method: "DELETE"
    });

    if (!result.success) {
      throw new Error(result.message || "Unable to delete ritual");
    }
  }
};

const workflowService: WorkflowService = {
  async listWorkflows(_username: string): Promise<WorkflowSummary[]> {
    void _username;
    const result = await apiRequest<{ success: boolean; workflows?: unknown[] }>("/api/workflow/list");
    if (!result.success) {
      throw new Error("Unable to load workflows");
    }
    return (result.workflows ?? []).map(mapWorkflowSummary);
  },

  async getWorkflow(username: string, id: string): Promise<Workflow> {
    const summaries = await workflowService.listWorkflows(username);
    const summary = summaries.find((item) => item.id === id);

    let rawWorkflow: unknown = null;
    try {
      const result = await apiRequest<{ success: boolean; workflow?: unknown; message?: string }>(
        `/api/workflow/${encodeURIComponent(id)}`
      );
      if (result.success && result.workflow) {
        rawWorkflow = result.workflow;
      }
    } catch {
      rawWorkflow = null;
    }

    return {
      id,
      name: summary?.name ?? `Workflow ${id.slice(0, 8)}`,
      description: summary?.description ?? "No description provided",
      status: summary?.status ?? "draft",
      updatedAt: summary?.updatedAt ?? new Date().toISOString(),
      json: formatWorkflowJson(rawWorkflow)
    };
  },

  async createWorkflow(_username: string, draft: WorkflowDraft): Promise<Workflow> {
    const result = await apiRequest<{ success: boolean; workflow_id?: string; status?: string; message?: string; error?: string }>(
      "/api/workflow/register",
      {
        method: "POST",
        body: {
          workflow_name: draft.name,
          workflow_description: draft.description,
          workflow_json: draft.json,
          register: false
        }
      }
    );

    if (!result.success || !result.workflow_id) {
      throw new Error(result.error || result.message || "Unable to create workflow");
    }

    return {
      id: result.workflow_id,
      name: draft.name,
      description: draft.description,
      status: result.status === "active" || result.status === "archived" ? result.status : "draft",
      updatedAt: new Date().toISOString(),
      json: draft.json
    };
  },

  async registerWorkflow(_username: string, id: string, draft?: WorkflowDraft): Promise<Workflow> {
    const result = await apiRequest<{ success: boolean; workflow_id?: string; status?: string; message?: string; error?: string }>(
      "/api/workflow/register",
      {
        method: "POST",
        body: {
          workflow_id: id,
          workflow_name: draft?.name,
          workflow_description: draft?.description,
          workflow_json: draft?.json,
          register: true
        }
      }
    );

    if (!result.success || !result.workflow_id) {
      throw new Error(result.error || result.message || "Unable to register workflow");
    }

    if (draft) {
      return {
        id: result.workflow_id,
        name: draft.name,
        description: draft.description,
        status: "active",
        updatedAt: new Date().toISOString(),
        json: draft.json
      };
    }

    const current = await workflowService.getWorkflow(_username, result.workflow_id);
    return { ...current, status: "active" };
  },

  async generateWorkflow(_username: string, input: WorkflowGenerationInput): Promise<string> {
    const result = await apiRequest<{ success: boolean; workflow_json?: string; error?: string; message?: string }>(
      "/api/workflow/generate",
      {
        method: "POST",
        body: {
          description: input.description,
          inputs: input.inputs,
          target_output_examples: input.targetOutputExamples,
          refine_mode: input.refineMode === true,
          refine_instruction: input.refineInstructions,
          refine_instructions: input.refineInstructions,
          existing_principles: input.existingPrinciples,
          existing_execution: input.existingExecution,
          existing_examples: input.existingExamples
        }
      }
    );

    if (!result.success || !result.workflow_json) {
      throw new Error(result.error || result.message || "Unable to generate workflow");
    }

    return result.workflow_json;
  },

  async updateWorkflow(username: string, id: string, draft: WorkflowDraft): Promise<Workflow> {
    const result = await apiRequest<{ success: boolean; workflow_id?: string; status?: string; message?: string; error?: string }>(
      "/api/workflow/register",
      {
        method: "POST",
        body: {
          workflow_id: id,
          workflow_name: draft.name,
          workflow_description: draft.description,
          workflow_json: draft.json,
          register: false
        }
      }
    );
    if (!result.success || !result.workflow_id) {
      throw new Error(result.error || result.message || "Unable to update workflow");
    }
    return {
      id: result.workflow_id,
      name: draft.name,
      description: draft.description,
      status: result.status === "active" || result.status === "archived" ? result.status : "draft",
      updatedAt: new Date().toISOString(),
      json: draft.json
    };
  },

  async deleteWorkflow(_username: string, id: string): Promise<void> {
    const result = await apiRequest<{ success: boolean; message?: string }>(`/api/workflow/${encodeURIComponent(id)}`, {
      method: "DELETE"
    });

    if (!result.success) {
      throw new Error(result.message || "Unable to delete workflow");
    }
  }
};

const profileService: ProfileService = {
  async getProfile(): Promise<User> {
    const result = await apiRequest<{ success: boolean; user?: unknown; message?: string }>("/api/profile/me");
    if (!result.success || !result.user) {
      throw new Error(result.message || "Profile not found");
    }
    return mapUser(result.user);
  },

  async updateProfile(_username: string, patch: Partial<User>): Promise<User> {
    const payload: Record<string, unknown> = {};
    if (patch.email !== undefined) payload.email = patch.email;
    if (patch.department !== undefined) payload.department = patch.department;
    if (patch.analyst !== undefined) payload.analyst = patch.analyst;

    const result = await apiRequest<{ success: boolean; message?: string }>("/api/profile/update", {
      method: "PUT",
      body: payload
    });
    if (!result.success) {
      throw new Error(result.message || "Unable to update profile");
    }

    return profileService.getProfile("me");
  },

  async getRawLlmLoggingPreference(): Promise<RawLlmLoggingPreference> {
    const result = await apiRequest<BackendRawLlmLoggingPreferenceResponse>("/api/profile/logging/raw-llm");
    if (!result.success) {
      throw new Error(
        typeof result.message === "string" ? result.message : "Unable to load raw LLM logging preference"
      );
    }
    return { enabled: result.enabled === true };
  },

  async setRawLlmLoggingPreference(enabled: boolean): Promise<RawLlmLoggingPreference> {
    const result = await apiRequest<BackendRawLlmLoggingPreferenceResponse>("/api/profile/logging/raw-llm", {
      method: "PUT",
      body: { enabled }
    });
    if (!result.success) {
      throw new Error(
        typeof result.message === "string" ? result.message : "Unable to update raw LLM logging preference"
      );
    }
    return { enabled: result.enabled === true };
  },

  async getAnalysts(): Promise<string[]> {
    const result = await apiRequest<{ success: boolean; analysts?: string[] }>("/api/profile/analysts");
    if (!result.success) throw new Error("Failed to load analysts");
    return result.analysts || [];
  },

  async getAnalystCoverage(analyst?: string): Promise<Array<Record<string, unknown>>> {
    const query = analyst ? `?analyst=${encodeURIComponent(analyst)}` : "";
    const result = await apiRequest<{ success: boolean; coverage?: Array<Record<string, unknown>> }>(`/api/profile/coverage${query}`);
    if (!result.success) throw new Error("Failed to load coverage");
    return result.coverage || [];
  },

  async getPeerGroup(ticker: string, analyst?: string): Promise<unknown[]> {
    const query = new URLSearchParams({ ticker });
    if (analyst) query.set("analyst", analyst);
    const result = await apiRequest<{ success: boolean; peers?: unknown[] }>(`/api/profile/peers?${query.toString()}`);
    if (!result.success) throw new Error("Failed to load peer group");
    return result.peers || [];
  },

  async updatePeerGroup(ticker: string, peers: string[], primaries: string[], analyst?: string): Promise<void> {
    const result = await apiRequest<{ success: boolean; message?: string }>("/api/profile/peers", {
      method: "POST",
      body: { ticker, peers, primaries, analyst }
    });
    if (!result.success) throw new Error(result.message || "Failed to update peer group");
  },

  async getCustomBenchmarks(analyst?: string): Promise<unknown[]> {
    const query = analyst ? `?analyst=${encodeURIComponent(analyst)}` : "";
    const result = await apiRequest<{ success: boolean; benchmarks?: unknown[] }>(`/api/profile/benchmarks/custom${query}`);
    if (!result.success) throw new Error("Failed to load custom benchmarks");
    return result.benchmarks || [];
  },

  async getMasterBenchmarks(): Promise<unknown[]> {
    const result = await apiRequest<{ success: boolean; benchmarks?: unknown[] }>("/api/profile/benchmarks/master");
    if (!result.success) throw new Error("Failed to load master benchmarks");
    return result.benchmarks || [];
  },

  async createCustomBenchmark(name: string, weighting: string, description: string, analyst?: string): Promise<string> {
    const result = await apiRequest<{ success: boolean; bench_id?: string; message?: string }>("/api/profile/benchmarks/custom", {
      method: "POST",
      body: { name, weighting, description, analyst }
    });
    if (!result.success || !result.bench_id) throw new Error(result.message || "Failed to create benchmark");
    return result.bench_id;
  },

  async deleteCustomBenchmark(benchId: string, analyst?: string): Promise<void> {
    const query = analyst ? `?analyst=${encodeURIComponent(analyst)}` : "";
    const result = await apiRequest<{ success: boolean; message?: string }>(`/api/profile/benchmarks/custom/${encodeURIComponent(benchId)}${query}`, {
      method: "DELETE"
    });
    if (!result.success) throw new Error(result.message || "Failed to delete benchmark");
  },

  async getBenchmarkMembers(benchId: string, analyst?: string): Promise<{ tickers: string[]; sectors: string[]; industries: string[] }> {
    const query = analyst ? `?analyst=${encodeURIComponent(analyst)}` : "";
    const result = await apiRequest<{
      success: boolean;
      members?: { tickers?: string[]; sectors?: string[]; industries?: string[] };
    }>(`/api/profile/benchmarks/members/${encodeURIComponent(benchId)}${query}`);
    if (!result.success) throw new Error("Failed to load benchmark members");
    return {
      tickers: result.members?.tickers ?? [],
      sectors: result.members?.sectors ?? [],
      industries: result.members?.industries ?? []
    };
  },

  async getExistingCoverageTickers(benchId: string, analyst?: string): Promise<string[]> {
    const query = analyst ? `?analyst=${encodeURIComponent(analyst)}` : "";
    const result = await apiRequest<{ success: boolean; tickers?: string[]; message?: string }>(
      `/api/profile/benchmarks/applied-tickers/${encodeURIComponent(benchId)}${query}`
    );
    if (!result.success) throw new Error(result.message || "Failed to load applied coverage tickers");
    return Array.isArray(result.tickers) ? result.tickers.filter((item): item is string => typeof item === "string") : [];
  },

  async updateBenchmarkMembers(
    benchId: string,
    tickers: string[],
    sectors: string[],
    industries: string[],
    analyst?: string
  ): Promise<void> {
    const result = await apiRequest<{ success: boolean; message?: string }>(`/api/profile/benchmarks/members/${encodeURIComponent(benchId)}`, {
      method: "POST",
      body: { tickers, sectors, industries, analyst }
    });
    if (!result.success) throw new Error(result.message || "Failed to update benchmark members");
  },

  async getCoverageBenchmarks(ticker: string, analyst?: string): Promise<unknown[]> {
    const query = new URLSearchParams({ ticker });
    if (analyst) query.set("analyst", analyst);
    const result = await apiRequest<{ success: boolean; benchmarks?: unknown[] }>(`/api/profile/benchmarks/coverage?${query.toString()}`);
    if (!result.success) throw new Error("Failed to load coverage benchmarks");
    return result.benchmarks || [];
  },

  async applyBenchmarkToCoverage(
    benchId: string,
    tickers: string[],
    sectors: string[],
    industries: string[],
    makePrimary: boolean,
    analyst?: string
  ): Promise<void> {
    const result = await apiRequest<{ success: boolean; message?: string }>("/api/profile/benchmarks/apply", {
      method: "POST",
      body: { bench_id: benchId, tickers, sectors, industries, make_primary: makePrimary, analyst }
    });
    if (!result.success) throw new Error(result.message || "Failed to apply benchmark");
  },

  async setCompanyBenchmarks(ticker: string, benchIds: string[], primaryIds: string[], analyst?: string): Promise<void> {
    const result = await apiRequest<{ success: boolean; message?: string }>("/api/profile/benchmarks/company", {
      method: "POST",
      body: { ticker, bench_ids: benchIds, primary_ids: primaryIds, analyst }
    });
    if (!result.success) throw new Error(result.message || "Failed to update company benchmarks");
  }
};

const adminService: AdminService = {
  async listUsers(): Promise<User[]> {
    const result = await apiRequest<{ success: boolean; users?: unknown[]; message?: string }>("/api/admin/users");
    if (!result.success) {
      throw new Error(result.message || "Unable to load users");
    }
    return (result.users ?? []).map(mapUser);
  },

  async listPendingUsers(): Promise<User[]> {
    const result = await apiRequest<{ success: boolean; pending_users?: unknown[]; message?: string }>(
      "/api/admin/pending-users"
    );
    if (!result.success) {
      throw new Error(result.message || "Unable to load pending users");
    }
    return (result.pending_users ?? []).map(mapUser);
  },

  async getUserDetails(username: string): Promise<User> {
    const result = await apiRequest<{ success: boolean; user?: unknown; message?: string }>(
      `/api/admin/user/${encodeURIComponent(username)}`
    );
    if (!result.success || !result.user) {
      throw new Error(result.message || "Unable to load user details");
    }
    return mapUser(result.user);
  },

  async approveUser(username: string): Promise<void> {
    const result = await apiRequest<{ success: boolean; message?: string }>("/api/admin/approve-user", {
      method: "POST",
      body: { target_username: username }
    });
    if (!result.success) {
      throw new Error(result.message || "Unable to approve user");
    }
  },

  async rejectUser(username: string): Promise<void> {
    const result = await apiRequest<{ success: boolean; message?: string }>("/api/admin/reject-user", {
      method: "POST",
      body: { target_username: username }
    });
    if (!result.success) {
      throw new Error(result.message || "Unable to reject user");
    }
  },

  async makeAdmin(username: string): Promise<void> {
    const result = await apiRequest<{ success: boolean; message?: string }>("/api/admin/make-admin", {
      method: "POST",
      body: { target_username: username }
    });
    if (!result.success) {
      throw new Error(result.message || "Unable to grant admin role");
    }
  }
};

const fileService: FileService = {
  async listSessionFiles(sessionId: string): Promise<UploadedFile[]> {
    const normalizedSessionId = normalizeSessionId(sessionId);
    const result = await apiRequest<{ success: boolean; files?: BackendFileReference[]; message?: string }>(
      `/api/files/session/${encodeURIComponent(normalizedSessionId)}`
    );
    if (!result.success) {
      throw new Error(result.message || "Unable to list session files");
    }
    return (result.files ?? []).map((fileReference) => toUploadedFile(normalizedSessionId, fileReference));
  },

  async uploadSessionFile(sessionId: string, file: File): Promise<UploadedFile> {
    const normalizedSessionId = normalizeSessionId(sessionId);
    const formData = new FormData();
    formData.append("session_id", normalizedSessionId);
    formData.append("file", file);

    const result = await apiRequest<{
      success: boolean;
      file_reference?: BackendFileReference;
      error?: string;
    }>("/api/files/upload", {
      method: "POST",
      body: formData
    });

    if (!result.success || !result.file_reference) {
      throw new Error(result.error || "Unable to upload file");
    }

    return toUploadedFile(normalizedSessionId, result.file_reference);
  },

  async removeSessionFile(sessionId: string, fileId: string): Promise<void> {
    const normalizedSessionId = normalizeSessionId(sessionId);
    const trimmedFileId = fileId.trim();
    if (!trimmedFileId) {
      throw new Error("Missing file identifier");
    }
    const result = await apiRequest<{ success: boolean; message?: string }>(
      `/api/files/session/${encodeURIComponent(normalizedSessionId)}?file_id=${encodeURIComponent(trimmedFileId)}`,
      { method: "DELETE" }
    );
    if (!result.success) {
      throw new Error(result.message || "Unable to remove uploaded file");
    }
  },

  async listCustomData(sessionId: string): Promise<CustomDataAsset[]> {
    const normalizedSessionId = normalizeSessionId(sessionId);
    const result = await apiRequest<{
      success: boolean;
      session_id?: string;
      custom_data?: Array<{
        id?: string;
        session_id?: string;
        filename?: string;
        factors?: string[];
        created_at?: string;
      }>;
    }>(`/api/custom-data/session/${encodeURIComponent(normalizedSessionId)}`);

    if (!result.success) {
      throw new Error("Unable to list custom data");
    }

    return (result.custom_data ?? []).map((item) => ({
      id: item.id ?? makeId("custom-data"),
      sessionId: item.session_id ?? normalizedSessionId,
      filename: item.filename ?? "custom-data.csv",
      factors: Array.isArray(item.factors) ? item.factors.filter((factor): factor is string => typeof factor === "string") : [],
      createdAt: normalizeDate(item.created_at)
    }));
  },

  async uploadCustomData(sessionId: string, file: File): Promise<CustomDataAsset> {
    const normalizedSessionId = normalizeSessionId(sessionId);
    const formData = new FormData();
    formData.append("session_id", normalizedSessionId);
    formData.append("file", file);

    const result = await apiRequest<{
      success: boolean;
      custom_data?: {
        id?: string;
        session_id?: string;
        filename?: string;
        factors?: string[];
        created_at?: string;
      };
      message?: string;
    }>("/api/custom-data/upload", {
      method: "POST",
      body: formData
    });

    if (!result.success || !result.custom_data) {
      throw new Error(result.message || "Unable to upload custom data");
    }

    const uploaded = result.custom_data;
    return {
      id: uploaded.id ?? makeId("custom-data"),
      sessionId: uploaded.session_id ?? normalizedSessionId,
      filename: uploaded.filename ?? file.name,
      factors: Array.isArray(uploaded.factors) ? uploaded.factors.filter((factor): factor is string => typeof factor === "string") : [],
      createdAt: normalizeDate(uploaded.created_at)
    };
  },

  async removeCustomData(sessionId: string, id: string): Promise<void> {
    const normalizedSessionId = normalizeSessionId(sessionId);
    const result = await apiRequest<{ success: boolean; message?: string }>(
      `/api/custom-data/session/${encodeURIComponent(normalizedSessionId)}/${encodeURIComponent(id)}`,
      { method: "DELETE" }
    );
    if (!result.success) {
      throw new Error(result.message || "Unable to delete custom data");
    }
  }
};

const providerService: ProviderService = {
  async getStatus(): Promise<ProviderStatusSummary> {
    const result = await apiRequest<BackendProvidersStatusResponse>("/api/providers/status");
    if (!result.success) {
      throw new Error("Unable to load provider status");
    }
    return mapProviderStatusSummary(result);
  },

  async getPreferences(): Promise<ProviderPreferences> {
    const result = await apiRequest<BackendProviderPreferencesResponse>("/api/providers/preferences");
    if (!result.success) {
      throw new Error("Unable to load provider preferences");
    }
    return mapProviderPreferences(result);
  },

  async setPreferences(input: { preferredProvider: RoutingProviderId; preferredModel?: string }): Promise<ProviderPreferences> {
    const preferredModel = cleanOptionalText(input.preferredModel);
    const result = await apiRequest<BackendProviderPreferencesResponse>("/api/providers/preferences", {
      method: "PUT",
      body: {
        preferred_provider: input.preferredProvider,
        preferred_model: preferredModel
      }
    });
    if (!result.success) {
      throw new Error("Unable to update provider preferences");
    }
    return mapProviderPreferences(result);
  },

  async getAuthStatus(provider: ProviderId): Promise<ProviderRuntimeStatus & { user: string }> {
    const result = await apiRequest<BackendProviderAuthStatusResponse>(
      `/api/providers/${encodeURIComponent(provider)}/auth-status`
    );
    if (!result.success) {
      throw new Error("Unable to load provider auth status");
    }
    return {
      user: typeof result.user === "string" ? result.user : "unknown",
      ...mapProviderRuntimeStatus(provider, result)
    };
  },

  async startLogin(
    provider: ProviderId,
    input?: { forceRestart?: boolean; email?: string }
  ): Promise<ProviderLoginSession> {
    const result = await apiRequest<BackendProviderLoginSessionResponse>(
      `/api/providers/${encodeURIComponent(provider)}/login/start`,
      {
        method: "POST",
        body: {
          force_restart: input?.forceRestart === true,
          email: cleanOptionalText(input?.email)
        }
      }
    );
    return mapProviderLoginSession(result, provider);
  },

  async getLatestLogin(provider: ProviderId): Promise<ProviderLoginSession> {
    const result = await apiRequest<BackendProviderLoginSessionResponse>(
      `/api/providers/${encodeURIComponent(provider)}/login`
    );
    return mapProviderLoginSession(result, provider);
  },

  async getLogin(loginId: string): Promise<ProviderLoginSession> {
    const result = await apiRequest<BackendProviderLoginSessionResponse>(
      `/api/providers/login/${encodeURIComponent(loginId)}`
    );
    return mapProviderLoginSession(result);
  },

  async sendLoginInput(loginId: string, input: { text: string; appendNewline?: boolean }): Promise<ProviderLoginSession> {
    const text = input.text.trim();
    if (!text) {
      throw new Error("Code cannot be empty");
    }
    const result = await apiRequest<BackendProviderLoginSessionResponse>(
      `/api/providers/login/${encodeURIComponent(loginId)}/input`,
      {
        method: "POST",
        body: {
          text,
          append_newline: input.appendNewline !== false
        }
      }
    );
    return mapProviderLoginSession(result);
  },

  async cancelLogin(loginId: string): Promise<ProviderLoginSession> {
    const result = await apiRequest<BackendProviderLoginSessionResponse>(
      `/api/providers/login/${encodeURIComponent(loginId)}/cancel`,
      {
        method: "POST"
      }
    );
    return mapProviderLoginSession(result);
  },

  async disconnectAll(): Promise<ProviderDisconnectResult> {
    const result = await apiRequest<BackendProviderDisconnectResponse>("/api/providers/disconnect", {
      method: "POST"
    });
    if (!result.success) {
      throw new Error("Unable to disconnect providers");
    }
    return {
      status: mapProviderStatusSummary(result),
      message:
        typeof result.message === "string" && result.message.trim()
          ? result.message.trim()
          : "Providers disconnected. Stella will now use Gemini ADK.",
      cancelledLoginIds: Array.isArray(result.cancelled_login_ids)
        ? result.cancelled_login_ids.filter((item): item is string => typeof item === "string")
        : []
    };
  }
};

const skillService: SkillService = {
  async getCatalog(): Promise<SkillCatalog> {
    const result = await apiRequest<BackendSkillCatalogResponse>("/api/skills/catalog");
    if (!result.success) {
      throw new Error("Unable to load skills catalog");
    }
    const skills = Array.isArray(result.skills)
      ? result.skills.map(mapSkillCatalogItem).filter((item): item is SkillCatalogItem => item !== null)
      : [];
    return {
      generatedAt: cleanOptionalText(result.generated_at),
      repoRevision: cleanOptionalText(result.repo_revision),
      skills
    };
  },

  async syncCatalog(input?: { includePartnerBuilt?: boolean; plugins?: string[] }): Promise<{ message: string; result: SkillSyncResult }> {
    const result = await apiRequest<BackendSkillSyncResponse>("/api/skills/sync", {
      method: "POST",
      body: {
        include_partner_built: input?.includePartnerBuilt === true,
        plugins: Array.isArray(input?.plugins) ? input?.plugins : undefined
      }
    });
    if (!result.success) {
      throw new Error("Unable to sync skills catalog");
    }
    return {
      message: cleanOptionalText(result.message) ?? "Skills synced successfully.",
      result: mapSkillSyncResult(result.result)
    };
  },

  async enableSkill(skillId: string): Promise<void> {
    const result = await apiRequest<{ success: boolean; message?: string }>(
      `/api/skills/${encodeURIComponent(skillId)}/enable`,
      { method: "POST" }
    );
    if (!result.success) {
      throw new Error(result.message || "Unable to enable skill");
    }
  },

  async disableSkill(skillId: string): Promise<void> {
    const result = await apiRequest<{ success: boolean; message?: string }>(
      `/api/skills/${encodeURIComponent(skillId)}/disable`,
      { method: "POST" }
    );
    if (!result.success) {
      throw new Error(result.message || "Unable to disable skill");
    }
  }
};

const dashboardService: DashboardService = {
  async getStats(username: string): Promise<DashboardStats> {
    const [sessions, rituals, workflows] = await Promise.all([
      sessionService.listSessions(username),
      ritualService.listRituals(username),
      workflowService.listWorkflows(username)
    ]);

    const activeTheme = (document.documentElement.getAttribute("data-theme") as ThemeId) ?? "cybernetic-minimalism";

    return {
      totalSessions: sessions.length,
      totalRituals: rituals.length,
      totalWorkflows: workflows.length,
      activeTheme
    };
  }
};

const benchmarkService: BenchmarkService = {
  async getPeerGroups(analyst: string): Promise<PeerGroupsData> {
    const query = `?analyst=${encodeURIComponent(analyst)}`;
    const result = await apiRequest<{
      success: boolean;
      analyst?: string;
      tickers?: string[];
      peerGroups?: Record<string, unknown[]>;
      message?: string;
    }>(`/api/profile/peers/all${query}`);
    if (!result.success) {
      throw new Error(result.message || "Failed to load peer groups");
    }

    const tickers = Array.isArray(result.tickers)
      ? result.tickers.filter((item): item is string => typeof item === "string")
      : [];

    const peerGroups: Record<string, PeerInfo[]> = {};
    const rawPeerGroups = result.peerGroups ?? {};
    for (const ticker of tickers) {
      const rawPeers = Array.isArray(rawPeerGroups[ticker]) ? rawPeerGroups[ticker] : [];
      peerGroups[ticker] = rawPeers.map(mapPeerInfo).filter((peer): peer is PeerInfo => peer !== null);
    }

    return {
      analyst: result.analyst || analyst,
      tickers,
      peerGroups
    };
  },

  async updatePeerGroup(analyst: string, ticker: string, peers: string[], primaryPeers: string[]): Promise<void> {
    await profileService.updatePeerGroup(ticker, peers, primaryPeers, analyst);
  },

  async getCustomBenchmarks(analyst: string): Promise<CustomBenchmark[]> {
    const benchmarks = await profileService.getCustomBenchmarks(analyst);
    const mapped = benchmarks.map(mapCustomBenchmarkEntry).filter((item): item is CustomBenchmark => item !== null);
    await Promise.all(
      mapped.map(async (benchmark) => {
        try {
          const members = await profileService.getBenchmarkMembers(benchmark.id, analyst);
          benchmark.tickers = members.tickers;
        } catch {
          benchmark.tickers = [];
        }
      })
    );
    return mapped;
  },

  async getCompanyCoverage(analyst: string): Promise<CompanyBenchmark[]> {
    const coverage = await profileService.getAnalystCoverage(analyst);
    const seen = new Set<string>();
    const companies = coverage
      .map(mapCoverageCompanyEntry)
      .filter((company): company is CompanyBenchmark => company !== null)
      .filter((company) => {
        if (seen.has(company.ticker)) return false;
        seen.add(company.ticker);
        return true;
      });

    await Promise.all(
      companies.map(async (company) => {
        const benchmarks = await profileService.getCoverageBenchmarks(company.ticker, analyst);
        company.benchmarks = benchmarks
          .map(extractBenchmarkName)
          .filter(Boolean);
      })
    );

    return companies.sort((a, b) => a.ticker.localeCompare(b.ticker));
  }
};

export const apiClient = {
  agents: agentService,
  auth: authService,
  sessions: sessionService,
  chat: chatService,
  rituals: ritualService,
  workflows: workflowService,
  profile: profileService,
  admin: adminService,
  files: fileService,
  dashboard: dashboardService,
  benchmarks: benchmarkService,
  providers: providerService,
  skills: skillService
};
